/**
 * Firebase servisi
 * İki farklı Firebase yapılandırması ile çalışır:
 * 1. Auth DB: Kullanıcı kimlik doğrulama ve kullanıcı bilgilerini çekmek için
 * 2. Chat DB: Mesajlaşma, depolama ve yönetim için
 */

import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
} from 'firebase/auth';
import { 
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  setDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  deleteDoc,
  startAfter,
  writeBatch,
  increment,
  Timestamp
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
  listAll
} from 'firebase/storage';

// Firebase konfigürasyonları
const authDbConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const chatDbConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_CHAT_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_CHAT_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_CHAT_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_CHAT_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_CHAT_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_CHAT_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_CHAT_MEASUREMENT_ID
}

// Kullanıcı sohbet dinleyicisi: arşiv istemcide süzüldüğü için düşük limitte üst sıradaki
// kayıtlar çoğunlukla arşivlenmiş olabilir; aktif sohbetler hiç gelmez (yeni özel sohbet kaybolur).
const USER_CHATS_SNAPSHOT_LIMIT = 500

/** Firestore Timestamp / Date / sayı → milisaniye */
function firebaseTimestampToMs (ts) {
  if (!ts) return 0
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts.seconds === 'number') {
    return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6)
  }
  if (ts instanceof Date) return ts.getTime()
  if (typeof ts === 'number') return ts
  return 0
}

const STALE_PRIVATE_CHAT_DAYS_DEFAULT = 90

const DELETE_BATCH_SIZE = 300
const STORAGE_DELETE_CONCURRENCY = 8
let maintenanceLock = null

function acquireMaintenance (label) {
  if (maintenanceLock) {
    throw new Error('Başka bir silme işlemi sürüyor. Bitmesini bekleyin.')
  }
  maintenanceLock = label
}

function releaseMaintenance () {
  maintenanceLock = null
}
const FILE_MESSAGE_TYPES = new Set(['image', 'video', 'document', 'file'])

function isChatFileUrl (value) {
  if (typeof value !== 'string' || value.length < 12) {
    return false
  }
  let decoded = value
  try {
    decoded = decodeURIComponent(value)
  } catch (_) {
    decoded = value
  }
  return decoded.includes('chatFiles/')
}

function storageUrlsOnMessage (data) {
  if (!data || typeof data !== 'object') {
    return []
  }
  const urls = []
  if (isChatFileUrl(data.fileURL)) {
    urls.push(data.fileURL)
  }
  if (FILE_MESSAGE_TYPES.has(data.type) && isChatFileUrl(data.content)) {
    urls.push(data.content)
  }
  return [...new Set(urls)]
}

function fileNameTimestampMs (fileName) {
  const prefix = String(fileName || '').split('_')[0]
  if (!/^\d{12,}$/.test(prefix)) {
    return 0
  }
  const ms = Number(prefix)
  return Number.isFinite(ms) ? ms : 0
}

function rethrowFriendly (error) {
  const code = error?.code || ''
  const message = String(error?.message || '')
  if (code === 'permission-denied' || /insufficient permissions/i.test(message)) {
    const wrapped = new Error('Güvenlik kuralları bu silmeye izin vermiyor. İşlem uygulanamadı.')
    wrapped.cause = error
    throw wrapped
  }
  throw error
}

async function deleteChatFileUrl (url) {
  if (!isChatFileUrl(url)) {
    return false
  }
  try {
    await deleteObject(ref(chatStorage, url))
    return true
  } catch (error) {
    if (error?.code === 'storage/object-not-found') {
      return false
    }
    console.warn('Sohbet dosyası silinemedi:', error?.code || error?.message || error)
    return false
  }
}

async function deleteUrlsPooled (urls) {
  const unique = [...new Set((urls || []).filter(isChatFileUrl))]
  let deleted = 0
  let cursor = 0
  const worker = async () => {
    while (cursor < unique.length) {
      const url = unique[cursor++]
      if (await deleteChatFileUrl(url)) {
        deleted++
      }
    }
  }
  const workers = Math.min(STORAGE_DELETE_CONCURRENCY, unique.length)
  if (workers > 0) {
    await Promise.all(Array.from({ length: workers }, () => worker()))
  }
  return deleted
}

async function commitDeletes (refsToDelete) {
  for (let i = 0; i < refsToDelete.length; i += DELETE_BATCH_SIZE) {
    const batch = writeBatch(chatDb)
    refsToDelete.slice(i, i + DELETE_BATCH_SIZE).forEach((itemRef) => {
      batch.delete(itemRef)
    })
    await batch.commit()
  }
}

/**
 * Koleksiyondaki belgeleri sayfa sayfa siler.
 * beforeMs verilirse yalnızca timestamp'i bu andan eski olan mesajlar silinir.
 */
async function drainCollection (colRef, options = {}) {
  const { beforeMs = null, deleteFiles = false, onProgress = null, maxRounds = 400 } = options
  let deletedDocs = 0
  let deletedFiles = 0
  const seen = new Set()

  for (let round = 0; round < maxRounds; round++) {
    const q = beforeMs != null
      ? query(
        colRef,
        where('timestamp', '<', Timestamp.fromMillis(beforeMs)),
        orderBy('timestamp', 'asc'),
        limit(DELETE_BATCH_SIZE)
      )
      : query(colRef, limit(DELETE_BATCH_SIZE))
    const snap = await getDocs(q)
    if (snap.empty) {
      return { deletedDocs, deletedFiles, incomplete: false }
    }
    const docs = snap.docs.filter((item) => !seen.has(item.id))
    if (docs.length === 0) {
      return { deletedDocs, deletedFiles, incomplete: true }
    }
    docs.forEach((item) => seen.add(item.id))
    if (deleteFiles) {
      const urls = []
      docs.forEach((item) => urls.push(...storageUrlsOnMessage(item.data())))
      deletedFiles += await deleteUrlsPooled(urls)
    }
    await commitDeletes(docs.map((item) => item.ref))
    deletedDocs += docs.length
    if (typeof onProgress === 'function') {
      onProgress({ deletedDocs, deletedFiles })
    }
    await new Promise((resolve) => setTimeout(resolve, 0))
    if (snap.size < DELETE_BATCH_SIZE) {
      return { deletedDocs, deletedFiles, incomplete: false }
    }
  }
  return { deletedDocs, deletedFiles, incomplete: true }
}

async function deleteStorageUnder (folderPath, options = {}) {
  const { olderThanMs = null, depth = 0 } = options
  if (depth > 4) {
    return 0
  }
  let listed
  try {
    listed = await listAll(ref(chatStorage, folderPath))
  } catch (error) {
    if (error?.code === 'storage/object-not-found') {
      return 0
    }
    if (depth > 0) {
      console.warn('Depolama klasörü listelenemedi:', folderPath, error?.code || error?.message)
      return 0
    }
    throw error
  }

  const victims = []
  listed.items.forEach((item) => {
    if (olderThanMs != null) {
      const ts = fileNameTimestampMs(item.name)
      if (!ts || ts >= olderThanMs) {
        return
      }
    }
    victims.push(item)
  })

  let deleted = 0
  let cursor = 0
  const worker = async () => {
    while (cursor < victims.length) {
      const item = victims[cursor++]
      try {
        await deleteObject(item)
        deleted++
      } catch (error) {
        if (error?.code !== 'storage/object-not-found') {
          console.warn('Dosya silinemedi:', item.fullPath, error?.code || error?.message)
        }
      }
    }
  }
  const workers = Math.min(STORAGE_DELETE_CONCURRENCY, victims.length)
  if (workers > 0) {
    await Promise.all(Array.from({ length: workers }, () => worker()))
  }

  for (const prefix of listed.prefixes) {
    deleted += await deleteStorageUnder(prefix.fullPath, {
      olderThanMs,
      depth: depth + 1
    })
  }
  return deleted
}

// Uygulamaları başlatmak için güvenli bir yol
let authApp, chatApp;

try {
  // [DEFAULT] veya 'auth' isimli uygulama var mı kontrol et
  try {
    // Eğer default app zaten varsa, onu authApp olarak kullan
    authApp = initializeApp(authDbConfig);
    //console.log("Default Firebase App başlatıldı");
  } catch (e) {
    // Eğer hata varsa, özel bir isimle başlat
    authApp = initializeApp(authDbConfig, 'auth');
    //console.log("Auth Firebase App başlatıldı");
  }
  
  // Chat uygulamasını başlat
  chatApp = initializeApp(chatDbConfig, 'chat');
  //console.log("Chat Firebase App başlatıldı");
} catch (error) {
  //console.error("Firebase uygulaması başlatılırken hata:", error);
}

if (!authApp || !chatApp) {
  throw new Error(
    'Firebase başlatılamadı. Kök dizinde .env dosyasında VITE_FIREBASE_* ve VITE_FIREBASE_CHAT_* değişkenlerinin tanımlı olduğundan emin olun.'
  )
}

/**
 * Firestore'u çok sekmeli IndexedDB önbelleği ile başlatır.
 * Eski enableMultiTabIndexedDbPersistence() yerine önerilen API kullanılır (konsol uyarısını önler).
 * Persistence desteklenmiyorsa veya zaten başlatılmışsa getFirestore ile devam eder.
 */
function createFirestoreWithMultiTabCache (app) {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    })
  } catch (err) {
    return getFirestore(app)
  }
}

// Auth ve Firestore servisleri
const auth = getAuth(authApp);
//console.log("Auth servisi başlatıldı, app:", auth.app.name);

const authDb = createFirestoreWithMultiTabCache(authApp);
//console.log("AuthDB servisi başlatıldı");

const chatDb = createFirestoreWithMultiTabCache(chatApp);
//console.log("ChatDB servisi başlatıldı");

const authStorage = getStorage(authApp);
//console.log("AuthStorage servisi başlatıldı");

const chatStorage = getStorage(chatApp);
//console.log("ChatStorage servisi başlatıldı");

/**
 * Başlıkta “çevrimdışı” sayılma eşiği (dakika).
 * ÖNEMLİ: Bu değer PRESENCE_WRITE_MIN_MS'ten (dakika cinsinden) BÜYÜK olmalı; aksi halde
 * aktif kullanıcı iki yazım arasında çevrimdışı görünür. Yazım 5dk → eşik 6dk.
 */
export const USER_PRESENCE_STALE_MINUTES = 6

/** Gruptan başka üyeyi çıkarabilecek kullanıcı kodları (userCode veya code, büyük/küçük harf duyarsız) */
const GROUP_REMOVE_MEMBER_ALLOWED_USER_CODES = new Set([
  'AB', 'SB', 'ZG', 'SE', 'MCK', 'GG', 'CO', 'MFE'
].map((c) => c.toUpperCase()))

export function userCanRemoveOthersFromGroup (profile) {
  if (!profile) {
    return false
  }
  const code = String(profile.userCode || profile.code || '').trim().toUpperCase()
  return GROUP_REMOVE_MEMBER_ALLOWED_USER_CODES.has(code)
}

export const FILE_SIZE_LIMIT_DEFAULT = 10 * 1024 * 1024
export const FILE_SIZE_LIMIT_ELEVATED = 50 * 1024 * 1024

export function isAdminOrControlProfile (profile) {
  if (!profile) {
    return false
  }
  const userRole = String(profile.userRole || '').toLowerCase()
  const role = String(profile.role || '').toLowerCase()
  return userRole === 'admin' || userRole === 'control' || role === 'admin' || role === 'control'
}

export function isAdminProfile (profile) {
  if (!profile) {
    return false
  }
  const userRole = String(profile.userRole || '').toLowerCase()
  const role = String(profile.role || '').toLowerCase()
  return userRole === 'admin' || role === 'admin'
}

export function describeGroupClose (groupName) {
  const name = String(groupName || 'Grup Sohbeti').trim() || 'Grup Sohbeti'
  return {
    name,
    confirm: `«${name}» grubu kalıcı olarak kapatılacak.\n\nTüm mesajlar, dosyalar ve grup kaydı silinir. Bu işlem geri alınamaz.`,
    prompt: `Onaylamak için grup adını aynen yazın:\n${name}`
  }
}

export function getMaxUploadSizeForProfile (profile) {
  return isAdminOrControlProfile(profile) ? FILE_SIZE_LIMIT_ELEVATED : FILE_SIZE_LIMIT_DEFAULT
}

/**
 * Profil veya sohbet userDetails parçasından son görülme (ms). Yalnızca lastSeenAt / lastActive.
 */
export function profileLastSeenToMs (profileOrDetail) {
  if (!profileOrDetail) {
    return 0
  }
  const raw = profileOrDetail.lastSeenAt ?? profileOrDetail.lastActive
  if (raw === undefined || raw === null || raw === '') {
    return 0
  }
  if (typeof raw.toDate === 'function') {
    const d = raw.toDate()
    return Number.isNaN(d.getTime()) ? 0 : d.getTime()
  }
  if (typeof raw === 'object' && typeof raw.seconds === 'number') {
    return raw.seconds * 1000 + Math.floor((raw.nanoseconds || 0) / 1e6)
  }
  if (typeof raw === 'number') {
    if (raw > 1e12) {
      return raw
    }
    if (raw > 1e9) {
      return raw * 1000
    }
    return 0
  }
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? 0 : d.getTime()
}

/**
 * Çevrim / liste noktası / grup modalı: yalnızca son görülmeye göre (manuel status yok).
 */
export function presenceMetaFromLastSeenOnly (profileOrDetail) {
  const lastMs = profileLastSeenToMs(profileOrDetail)
  if (!lastMs || lastMs <= 0) {
    return {
      label: 'Son görülme bilinmiyor',
      tier: 2,
      lastMs: 0,
      listDotClass: 'presence-dot-gray',
      modalDotClass: 'presence-dot'
    }
  }
  const diffMin = Math.max(0, Math.floor((Date.now() - lastMs) / 60000))
  if (diffMin <= USER_PRESENCE_STALE_MINUTES) {
    return {
      label: 'Şu an aktif',
      tier: 0,
      lastMs,
      listDotClass: 'presence-dot-green',
      modalDotClass: 'presence-dot presence-dot-on'
    }
  }
  if (diffMin < 60) {
    return {
      label: `${diffMin} dakika önce`,
      tier: 1,
      lastMs,
      listDotClass: 'presence-dot-red',
      modalDotClass: 'presence-dot presence-dot-busy'
    }
  }
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) {
    return {
      label: `${diffH} saat önce`,
      tier: 1,
      lastMs,
      listDotClass: 'presence-dot-gray',
      modalDotClass: 'presence-dot'
    }
  }
  const diffDays = Math.floor(diffH / 24)
  if (diffDays < 7) {
    return {
      label: `${diffDays} gün önce`,
      tier: 2,
      lastMs,
      listDotClass: 'presence-dot-gray',
      modalDotClass: 'presence-dot'
    }
  }
  return {
    label: 'Uzun süredir görülmedi',
    tier: 2,
    lastMs,
    listDotClass: 'presence-dot-gray',
    modalDotClass: 'presence-dot'
  }
}

// Auth durum gözlemcileri
const authStateObservers = [];

// Benzersiz kullanıcı kodu oluştur (6 karakterlik)
const generateUserCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let userCode = '';
  for (let i = 0; i < 6; i++) {
    userCode += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return userCode;
};

/**
 * Kullanıcı adı ve soyadı ile profil resmi oluşturur (gradient arka plan ve baş harfler)
 * @param {string} displayName - Kullanıcının adı ve soyadı
 * @returns {string} - Oluşturulan profil resminin base64 URL'i
 */
export function generateProfilePicture(displayName) {
  // Gradient için rastgele renkler
  const colors = [
    ['#FF9966', '#FF5E62'], // Turuncu-Kırmızı
    ['#56CCF2', '#2F80ED'], // Açık Mavi-Koyu Mavi
    ['#F2994A', '#F2C94C'], // Turuncu-Sarı
    ['#4158D0', '#C850C0'], // Mavi-Mor
    ['#00B4DB', '#0083B0'], // Turkuaz
    ['#FD746C', '#FF9068'], // Mercan-Turuncu
    ['#6A82FB', '#FC5C7D'], // Mavi-Pembe
    ['#134E5E', '#71B280'], // Koyu Turkuaz-Yeşil
    ['#5A3F37', '#2C7744'], // Kahverengi-Yeşil
    ['#764BA2', '#667EEA']  // Mor-Mavi
  ];
  
  // Rastgele bir gradient seç
  const randomIndex = Math.floor(Math.random() * colors.length);
  const gradient = colors[randomIndex];
  
  // Baş harfleri al
  let initials = '';
  if (displayName) {
    const nameParts = displayName.split(' ');
    if (nameParts.length >= 2) {
      // Ad ve soyad varsa her ikisinin baş harfini al
      initials = nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0);
    } else {
      // Sadece ad varsa ilk iki harfi al veya tek harf
      initials = nameParts[0].charAt(0) + (nameParts[0].charAt(1) || '');
    }
    initials = initials.toUpperCase();
  } else {
    initials = '?';
  }
  
  // SVG oluştur
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${gradient[0]}" />
        <stop offset="100%" stop-color="${gradient[1]}" />
      </linearGradient>
    </defs>
    <rect width="200" height="200" fill="url(#grad)" />
    <text x="50%" y="50%" dy=".1em" fill="white" font-family="Arial, sans-serif" font-size="80" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${initials}</text>
  </svg>`;
  
  // SVG'yi base64'e dönüştür
  const base64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  return base64;
}

// Kimlik Doğrulama Servisi
export const authService = {
  // Mevcut kimlik durumunu gözlemle
  onAuthStateChanged: (callback) => {
    authStateObservers.push(callback);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      callback(user);
    });
    return unsubscribe;
  },

  // Kullanıcı kaydı - Bunu SADECE Firebase Auth için kullanacağız
  register: async (email, password, displayName) => {
    try {
      //console.log("Kullanıcı kaydı başlatılıyor...");
      // Firebase Auth'ta kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Auth profilini güncelle
      await updateProfile(userCredential.user, { displayName });
      
      // authDb'deki kullanıcı bilgilerini OKUMA
      //console.log("Kullanıcı başarıyla kaydedildi, authDb'de bilgi aranıyor...");
      
      // SADECE chatDb'de kullanıcı profili oluştur
      // authDb'de kullanıcı bilgisi varsa chatDb'ye kopyala, yoksa yeni profil oluştur
      await syncUserToChatDb(userCredential.user.uid, {
        displayName,
        email
      });
      
      // E-posta doğrulama gönder
      await sendEmailVerification(userCredential.user);
      
      //console.log("Kullanıcı kaydı tamamlandı:", userCredential.user.uid);
      return userCredential.user;
    } catch (error) {
      //console.error("Kullanıcı kaydı sırasında hata:", error);
      throw error;
    }
  },

  // Giriş
  login: async (email, password) => {
    try {
      //console.log("Kullanıcı girişi başlatılıyor...");
      // Firebase Auth ile giriş yap
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      //console.log("Auth başarılı, kullanıcı ID:", userCredential.user.uid);
      
      // authDb'den kullanıcı bilgilerini OKU, varsa chatDb'de güncelle
      await syncUserToChatDb(userCredential.user.uid, {
        email,
        displayName: userCredential.user.displayName
      });
      
      return userCredential.user;
    } catch (error) {
      //console.error("Kullanıcı girişi sırasında hata:", error);
      throw error;
    }
  },

  // Çıkış
  logout: async () => {
    try {
      stopUserPresenceTracking()
      //console.log("Çıkış işlemi başlatılıyor...");
      const currentUser = auth.currentUser;
      
      // Kullanıcı durumunu değiştirmek için önce kullanıcı oturum açmış olmalı
      if (currentUser) {
        try {
          // ChatDb'deki kullanıcı durumunu güncelle
          //console.log(`${currentUser.uid} kullanıcısı için çevrimdışı durumunu ayarlama`);
          await updateChatUserStatus(currentUser.uid, 'Çevrimdışı');
        } catch (statusError) {
          // Durum güncellenemezse devam et, kritik değil
          //console.warn("Çıkış sırasında kullanıcı durumu güncellenemedi:", statusError);
        }
        
        // Firebase Auth'dan çıkış yap
        await signOut(auth);
        //console.log("Kullanıcı çıkışı başarıyla yapıldı");
        return true;
      } else {
        //console.warn("Çıkış yapılacak aktif kullanıcı yok");
        return false;
      }
    } catch (error) {
      //console.error("Çıkış sırasında hata:", error);
      // Yine de UI'da çıkış yapıldı olarak göstermek için true dönebiliriz
      return false;
    }
  },

  // Mevcut kullanıcı
  getCurrentUser: () => {
    return auth.currentUser;
  }
};

// OPTIMIZASYON: Cache (syncUserToChatDb öncesi — invalidate burada kullanılır)
const userProfileCache = new Map()
const chatMetadataCache = new Map() // Chat metadata cache
const CACHE_DURATION = 30 * 60 * 1000 // 30 dakika

function invalidateUserProfileCache (userId) {
  userProfileCache.delete(userId)
  try {
    localStorage.removeItem(`userProfile_${userId}`)
  } catch (e) {
    // yok say
  }
}

function attachLastActiveForProfile (profile) {
  if (!profile) {
    return null
  }
  // updatedAt profil alanları için güncellenir; çevrim varlığı için kullanılmaz (yanlış yeşil/nokta)
  const ts = profile.lastSeenAt ?? profile.lastActive
  if (ts === undefined || ts === null) {
    return { ...profile }
  }
  return { ...profile, lastActive: ts }
}

function getCachedUserProfile (userId) {
  const memoryCached = userProfileCache.get(userId)
  if (memoryCached && Date.now() - memoryCached.timestamp < CACHE_DURATION) {
    return memoryCached.data
  }

  try {
    const localStorageKey = `userProfile_${userId}`
    const localCached = localStorage.getItem(localStorageKey)
    if (localCached) {
      const parsed = JSON.parse(localCached)
      if (parsed && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        userProfileCache.set(userId, {
          data: parsed.data,
          timestamp: parsed.timestamp
        })
        return parsed.data
      }
      localStorage.removeItem(localStorageKey)
    }
  } catch (error) {
    console.warn('LocalStorage okuma hatası:', error)
  }

  return null
}

function setCachedUserProfile (userId, data) {
  userProfileCache.set(userId, {
    data,
    timestamp: Date.now()
  })

  try {
    const localStorageKey = `userProfile_${userId}`
    localStorage.setItem(localStorageKey, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch (error) {
    console.warn('LocalStorage yazma hatası:', error)
  }
}

// Auth'den chatDb'ye kullanıcı senkronizasyonu
// Bu fonksiyon authDb'den okur ve chatDb'ye yazar/günceller
// syncOpts.sessionKind: 'login' — durumu Çevrimiçi yapar | 'resume' — manuel durumu korur, yalnızca son görülme güncellenir
async function syncUserToChatDb(userId, userData, syncOpts = {}) {
  const sessionKind = syncOpts.sessionKind || 'login'
  try {
    //console.log("Kullanıcı chatDb senkronizasyonu başlatılıyor...");
    
    if (!userData.email) {
      //console.error("Email bilgisi olmadan kullanıcı senkronize edilemez!");
      return false;
    }
    
    // 1. AuthDb'den kullanıcı bilgilerini OKU - email ile filtreleme
    //console.log(`Email ile kullanıcı aranıyor: ${userData.email}`);
    const authUserQuery = query(
      collection(authDb, 'user'),
      where('email', '==', userData.email),
      limit(1)
    );
    
    const authUserSnapshot = await getDocs(authUserQuery);
    
    let chatUserData = {
      displayName: userData.displayName || '',
      email: userData.email || '',
      status: 'Çevrimiçi',
      updatedAt: serverTimestamp()
    };
    
    if (!authUserSnapshot.empty) {
      // AuthDb'de kullanıcı varsa, bilgileri al
      const authUserDoc = authUserSnapshot.docs[0];
      const authData = authUserDoc.data();
      //console.log("AuthDb'de kullanıcı bulundu, bilgiler alınıyor:", authData);
      
      // AuthDb'deki bilgileri chatDb için hazırla
      chatUserData = {
        ...chatUserData,
        userCode: authData.userCode || authData.code || '',
        userRole: authData.userRole || '',
        photoURL: authData.photoURL || '',
        // Diğer gerekli alanlar...
      };
      
      //console.log("AuthDb'den alınan kod:", chatUserData.userCode);
      //console.log("AuthDb'den alınan rol:", chatUserData.userRole);
    } else {
      //console.log("AuthDb'de kullanıcı bulunamadı, temel bilgilerle devam ediliyor");
      
      // Kullanıcı kodu oluştur
      if (!chatUserData.userCode) {
        chatUserData.userCode = generateUserCode();
        //console.log("Yeni kullanıcı kodu oluşturuldu:", chatUserData.userCode);
      }
    }
    
    // 2. ChatDb'de kullanıcı var mı kontrol et
    const chatUserDoc = await getDoc(doc(chatDb, 'users', userId));
    const userRef = doc(chatDb, 'users', userId)

    const profilePatch = {
      displayName: chatUserData.displayName || '',
      email: chatUserData.email,
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    if (chatUserData.userCode) {
      profilePatch.userCode = chatUserData.userCode
    }
    if (chatUserData.userRole) {
      profilePatch.userRole = chatUserData.userRole
    }
    if (chatUserData.photoURL) {
      profilePatch.photoURL = chatUserData.photoURL
    }

    if (chatUserDoc.exists()) {
      // ChatDb'de kullanıcı varsa profil + son görülme; oturum yenilemede manuel durumu ezme
      //console.log("ChatDb'de kullanıcı bulundu, durum güncelleniyor");
      if (sessionKind === 'resume') {
        await updateDoc(userRef, profilePatch)
      } else {
        await updateDoc(userRef, {
          ...profilePatch,
          status: 'Çevrimiçi'
        })
      }
      invalidateUserProfileCache(userId)
    } else {
      // ChatDb'de kullanıcı yoksa, yeni oluştur
      //console.log("ChatDb'de kullanıcı bulunamadı, yeni profil oluşturuluyor");
      
      // Profil resmi yok ise oluştur
      if (!chatUserData.photoURL && chatUserData.displayName) {
        chatUserData.photoURL = generateProfilePicture(chatUserData.displayName);
        //console.log("Kullanıcı için profil resmi oluşturuldu");
      }
      
      await setDoc(userRef, {
        ...chatUserData,
        lastSeenAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      invalidateUserProfileCache(userId)
    }
    
    //console.log("Kullanıcı senkronizasyonu tamamlandı");
    return true;
  } catch (error) {
    //console.error("Kullanıcı senkronizasyonu sırasında hata:", error);
    throw error;
  }
}

// ChatDb'deki kullanıcı durumunu günceller (SADECE chatDb)
async function updateChatUserStatus(userId, status) {
  try {
    await updateDoc(doc(chatDb, 'users', userId), {
      status,
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    invalidateUserProfileCache(userId)
    return true
  } catch (error) {
    //console.error("Kullanıcı durumu güncellenirken hata:", error);
    return false;
  }
}

/**
 * OPTIMIZASYON: Aynı oturumda gereksiz presence yazımını kesmek için minimum aralık (ms).
 * 2.5dk → 5dk: presence yazımları (ve bunları dinleyen herkesteki okumalar) ~%50 azalır.
 * USER_PRESENCE_STALE_MINUTES (6dk) bu değerden büyük olduğu için “aktif” noktası bozulmaz.
 */
const PRESENCE_WRITE_MIN_MS = 5 * 60 * 1000

let presenceLastWriteMs = 0
let presenceIntervalId = null
const presenceListenerDisposers = []

function stopUserPresenceTracking () {
  if (presenceIntervalId) {
    clearInterval(presenceIntervalId)
    presenceIntervalId = null
  }
  presenceListenerDisposers.forEach((fn) => fn())
  presenceListenerDisposers.length = 0
}

async function touchUserLastSeen (userId, opts = {}) {
  const force = opts.force === true
  const now = Date.now()
  if (!userId) {
    return false
  }
  if (!force && now - presenceLastWriteMs < PRESENCE_WRITE_MIN_MS) {
    return false
  }
  presenceLastWriteMs = now
  try {
    await updateDoc(doc(chatDb, 'users', userId), {
      lastSeenAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    invalidateUserProfileCache(userId)
    return true
  } catch (err) {
    return false
  }
}

function startUserPresenceTracking (userId) {
  stopUserPresenceTracking()
  presenceLastWriteMs = Date.now()

  const onVis = () => {
    if (document.visibilityState === 'visible' && auth.currentUser?.uid === userId) {
      touchUserLastSeen(userId, { force: false })
    }
  }
  document.addEventListener('visibilitychange', onVis)
  presenceListenerDisposers.push(() => document.removeEventListener('visibilitychange', onVis))

  const onPageHide = () => {
    if (auth.currentUser?.uid === userId) {
      touchUserLastSeen(userId, { force: true })
    }
  }
  window.addEventListener('pagehide', onPageHide)
  presenceListenerDisposers.push(() => window.removeEventListener('pagehide', onPageHide))

  presenceIntervalId = setInterval(() => {
    if (document.visibilityState === 'visible' && auth.currentUser?.uid === userId) {
      touchUserLastSeen(userId, { force: false })
    }
  }, PRESENCE_WRITE_MIN_MS)
}

// Kullanıcı Servisi
export const userService = {
  /**
   * ChatDb'de yeni kullanıcı profili oluşturur (SADECE chatDb)
   * @param {string} userId - Kullanıcı ID
   * @param {object} userData - Kullanıcı verisi
   * @returns {Promise}
   */
  async createChatUserProfile(userId, userData) {
    try {
      // Kullanıcı profil resmi yoksa, ismine göre oluştur
      if (!userData.photoURL && userData.displayName) {
        userData.photoURL = generateProfilePicture(userData.displayName);
      }
      
      // Oluşturma zamanı ekle
      const data = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // SADECE ChatDb'deki users koleksiyonuna ekle
      await setDoc(doc(chatDb, 'users', userId), data);
      
      return { id: userId, ...data };
    } catch (error) {
      //console.error('ChatDb kullanıcı profili oluşturulurken hata:', error);
      throw error;
    }
  },
  
  /**
   * Kullanıcı profilini getirir
   * @param {string} userId - Kullanıcı ID, e-posta veya kullanıcı adı
   * @param {{ skipCache?: boolean }} [opts] - skipCache: çevrimiçi başlığı gibi güncel alanlar için önbelleği atla
   * @returns {Promise<object|null>} - Kullanıcı profili veya null
   */
  async getUserProfile (userId, opts = {}) {
    try {
      if (!userId) {
        //console.error("getUserProfile: Kullanıcı ID boş olamaz");
        return null;
      }
      
      if (!opts.skipCache) {
        const cached = getCachedUserProfile(userId)
        if (cached) {
          return attachLastActiveForProfile(cached)
        }
      }
      
      //console.log(`Kullanıcı profili getiriliyor: ${userId}`);
      let userProfile = null;
      
      // 1. Önce ChatDB'de doğrudan belge ID'si (kullanıcı adı) ile arama
      try {
        const directChatUserRef = doc(chatDb, 'users', userId);
        const directChatUserSnap = await getDoc(directChatUserRef);
        
        if (directChatUserSnap.exists()) {
          userProfile = directChatUserSnap.data();
          //console.log(`ChatDB'de doğrudan ID ile kullanıcı bulundu: ${userId}`);
          const result = { id: userId, ...userProfile };
          if (!opts.skipCache) {
            setCachedUserProfile(userId, result)
          }
          return attachLastActiveForProfile(result)
        } else {
          //console.log(`ChatDB'de doğrudan ID ile kullanıcı bulunamadı: ${userId}, diğer yöntemlerle aranıyor...`);
        }
      } catch (error) {
        //console.log(`ChatDB'den doğrudan ID ile kullanıcı profili alınırken hata: ${error.message}`);
      }
      
      // 2. ChatDB'de UUID ile arama (aynı arama yöntemi, sadece adlandırma farklı)
      try {
        // İlk aramada bulunamadıysa, aynı ID ile tekrar aramaya gerek yok
        // Bu adımı atlayabiliriz veya farklı bir koleksiyonda arayabiliriz
      } catch (error) {
        //console.log(`ChatDB'den UUID ile kullanıcı profili alınırken hata: ${error.message}`);
      }
      
      // 3. Mevcut auth kullanıcısını kontrol et
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
          //console.log(`Mevcut auth kullanıcısı bulundu: ${userId}`);
          
          // Eğer ChatDb'de bu kullanıcı için bilgi varsa, onu al
          const chatUserDoc = await getDoc(doc(chatDb, 'users', userId));
          if (chatUserDoc.exists()) {
            userProfile = chatUserDoc.data();
            const result = { id: userId, ...userProfile };
            if (!opts.skipCache) {
              setCachedUserProfile(userId, result)
            }
            return attachLastActiveForProfile(result)
          }
          
          // ChatDb'de yoksa, auth kullanıcı bilgilerinden oluştur
          const result = { 
            id: userId,
            displayName: currentUser.displayName || '',
            email: currentUser.email || '',
            photoURL: currentUser.photoURL || '',
            emailVerified: currentUser.emailVerified || false
          };
          if (!opts.skipCache) {
            setCachedUserProfile(userId, result)
          }
          return attachLastActiveForProfile(result)
        }
      } catch (error) {
        //console.log(`Mevcut auth kullanıcısı kontrolünde hata: ${error.message}`);
      }
      
      // 4. AuthDB'de kullanıcı arama - email ile filtreleme
      try {
        // Kullanıcı ID'si bir email adresi mi kontrol et
        if (userId.includes('@')) {
          // Email ile doğrudan ara
          const emailQuery = query(
            collection(authDb, 'user'),
            where('email', '==', userId),
            limit(1)
          );
          
          const emailSnapshot = await getDocs(emailQuery);
          if (!emailSnapshot.empty) {
            userProfile = emailSnapshot.docs[0].data();
            //console.log(`AuthDB'de email ile kullanıcı bulundu: ${userId}`);
            return attachLastActiveForProfile({ id: emailSnapshot.docs[0].id, ...userProfile })
          }
        } else {
          // AuthDb'deki mevcut auth kullanıcısını bulmaya çalış
          // Önce currentUser'dan email'i al (eğer mevcutsa)
          let userEmail = null;
          const currentUser = auth.currentUser;
          
          if (currentUser && currentUser.uid === userId) {
            userEmail = currentUser.email;
            //console.log(`Mevcut kullanıcı emaili: ${userEmail}`);
          }
          
          if (userEmail) {
            // Email ile kullanıcıyı bul
            const emailQuery = query(
              collection(authDb, 'user'),
              where('email', '==', userEmail),
              limit(1)
            );
            
            const emailSnapshot = await getDocs(emailQuery);
            if (!emailSnapshot.empty) {
              userProfile = emailSnapshot.docs[0].data();
              //console.log(`AuthDB'de email ile kullanıcı bulundu: ${userEmail}`);
              return attachLastActiveForProfile({ id: emailSnapshot.docs[0].id, ...userProfile })
            }
          }
          
          // Email bulunamadıysa veya kullanıcı kodu ile ara
          // Code ile arama (eski)
          const codeQuery = query(
            collection(authDb, 'user'),
            where('code', '==', userId.toUpperCase()),
            limit(1)
          );
          
          const codeSnapshot = await getDocs(codeQuery);
          if (!codeSnapshot.empty) {
            userProfile = codeSnapshot.docs[0].data();
            //console.log(`AuthDB'de kod ile kullanıcı bulundu: ${userId}`);
            return attachLastActiveForProfile({ id: codeSnapshot.docs[0].id, ...userProfile })
          }
          
          // userCode ile arama (yeni)
          const userCodeQuery = query(
            collection(authDb, 'user'),
            where('userCode', '==', userId.toUpperCase()),
            limit(1)
          );
          
          const userCodeSnapshot = await getDocs(userCodeQuery);
          if (!userCodeSnapshot.empty) {
            userProfile = userCodeSnapshot.docs[0].data();
            //console.log(`AuthDB'de userCode ile kullanıcı bulundu: ${userId}`);
            return attachLastActiveForProfile({ id: userCodeSnapshot.docs[0].id, ...userProfile })
          }
        }
        
        //console.log(`Hiçbir veritabanında kullanıcı bulunamadı: ${userId}`);
        return null;
      } catch (error) {
        //console.error(`Kullanıcı profili alınırken hata: ${error.message}`);
        return null;
      }
    } catch (error) {
      //console.error('Kullanıcı profili alınırken hata:', error);
      return null;
    }
  },
  
  /**
   * Tüm kullanıcıları AuthDb'den getirir (SADECE OKUMA)
   * @param {string} currentUserId - Mevcut kullanıcı ID
   * @returns {Promise<Array>} - Kullanıcı listesi
   */
  async getAllUsers(currentUserId) {
    try {
      // AuthDb'den tüm kullanıcıları getir - SADECE OKUMA
      const usersSnap = await getDocs(collection(authDb, 'user'));
      const users = [];
      
      usersSnap.forEach(doc => {
        if (doc.id !== currentUserId) {
          users.push({
            id: doc.id,
            ...doc.data()
          });
        }
      });
      
      return users;
    } catch (error) {
      //console.error('Kullanıcılar getirilirken hata:', error);
      throw error;
    }
  },
  
  /**
   * Koda göre kullanıcı arar (AuthDb'den OKUMA)
   * @param {string} code - Kullanıcı kodu
   * @returns {Promise<object|null>} - Bulunan kullanıcı veya null
   */
  async findUserByCode(code) {
    try {
      // AuthDb'den kullanıcı koduna göre ara - SADECE OKUMA
      const q = query(
        collection(authDb, 'user'),
        where('code', '==', code.toUpperCase()),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
      
      return null;
    } catch (error) {
      //console.error('Kullanıcı aranırken hata:', error);
      throw error;
    }
  },
  
  /**
   * E-posta adresine göre kullanıcı bilgilerini getirir (AuthDb'den OKUMA)
   * @param {string} email - Kullanıcı e-posta adresi
   * @returns {Promise<object|null>} - Kullanıcı verisi veya null
   */
  async getUserByEmail(email) {
    try {
      // AuthDb'den e-posta adresine göre ara - SADECE OKUMA
      const q = query(
        collection(authDb, 'user'),
        where('email', '==', email),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }
      
      return null;
    } catch (error) {
      //console.error('E-posta ile kullanıcı aranırken hata:', error);
      throw error;
    }
  },
  
  /**
   * Kullanıcı durumunu ChatDb'de günceller (SADECE ChatDb)
   * @param {string} userId - Kullanıcı ID
   * @param {string} status - Yeni durum (Çevrimiçi, Meşgul, Uzakta). Çevrimdışı yalnızca çıkış akışında ayarlanır.
   * @returns {Promise<boolean>}
   */
  async updateUserStatus (userId, status) {
    if (status === 'Çevrimdışı') {
      console.warn('Çevrimdışı durumu kullanıcı tarafından ayarlanamaz')
      return false
    }
    return updateChatUserStatus(userId, status)
  },

  /**
   * Kalıcı oturum / sayfa yenileme sonrası ChatDb senkronu ve düşük maliyetli varlık dinleyicileri
   */
  async resumeSessionAfterAuth (user) {
    if (!user?.uid) {
      return false
    }
    if (user.email) {
      try {
        await syncUserToChatDb(user.uid, {
          email: user.email,
          displayName: user.displayName || ''
        }, { sessionKind: 'resume' })
      } catch (err) {
        console.warn('ChatDb oturum senkronu atlandı:', err)
      }
    }
    // Senkron başarısız olsa bile son görülme güncellemeleri çalışsın
    startUserPresenceTracking(user.uid)
    return true
  },

  stopPresenceTracking () {
    stopUserPresenceTracking()
  },

  /**
   * Kullanıcıları kod, email veya doğrudan ID/kullanıcı adı ile ara (öncelikli olarak ChatDb'den)
   * @param {string} search - Aranacak kod, email veya kullanıcı adı
   * @returns {Promise<Array>} - Kullanıcı listesi
   */
  async findUsersByCodeOrEmail(search) {
    try {
      if (!search || search.trim() === '') {
        return [];
      }
      
      search = search.trim().toLowerCase();
      const results = new Map();
      const isEmailSearch = search.includes('@');
      const foundEmails = new Set(); // Email takibi için
      const foundCodes = new Set();  // Kullanıcı kodu takibi için
      
      // ChatDb'de arama yap (öncelikli kaynak)
      try {
        const chatQueries = [];
        
        // Email ile arama - ChatDb
        if (isEmailSearch) {
          const chatEmailQuery = query(
            collection(chatDb, 'users'),
            where('email', '>=', search),
            where('email', '<=', search + '\uf8ff'),
            limit(10)
          );
          chatQueries.push(getDocs(chatEmailQuery));
        }
        
        // userCode ile arama - ChatDb
        const chatUserCodeQuery = query(
          collection(chatDb, 'users'),
          where('userCode', '>=', search.toUpperCase()),
          where('userCode', '<=', search.toUpperCase() + '\uf8ff'),
          limit(10)
        );
        chatQueries.push(getDocs(chatUserCodeQuery));
        
        // code ile arama - ChatDb
        const chatCodeQuery = query(
          collection(chatDb, 'users'),
          where('code', '>=', search.toUpperCase()),
          where('code', '<=', search.toUpperCase() + '\uf8ff'),
          limit(10)
        );
        chatQueries.push(getDocs(chatCodeQuery));
        
        // ChatDb sorgularını çalıştır
        const chatSnapshots = await Promise.all(chatQueries);
        
        // ChatDb sonuçlarını ekle ve email/code takibi yap
        let chatResultsCount = 0;
        chatSnapshots.forEach(snapshot => {
          chatResultsCount += snapshot.size;
          snapshot.forEach(doc => {
            const userData = doc.data();
            userData.source = 'chatDb';
            
            // Email ve kod bilgilerini kaydet
            if (userData.email) foundEmails.add(userData.email.toLowerCase());
            if (userData.userCode) foundCodes.add(userData.userCode.toUpperCase());
            if (userData.code) foundCodes.add(userData.code.toUpperCase());
            
            results.set(doc.id, { id: doc.id, ...userData });
          });
        });
        
        //console.log(`ChatDb'de ${chatResultsCount} kullanıcı bulundu`);
      } catch (chatError) {
        //console.error("ChatDb'de arama yaparken hata:", chatError);
      }
      
      // AuthDb'de arama yap (sadece ChatDb'de bulunmayan kullanıcılar için)
      try {
        const authQueries = [];
        
        // Email ile arama - AuthDb
        if (isEmailSearch) {
          const authEmailQuery = query(
            collection(authDb, 'user'),
            where('email', '>=', search),
            where('email', '<=', search + '\uf8ff'),
            limit(10)
          );
          authQueries.push(getDocs(authEmailQuery));
        }
        
        // userCode ile arama - AuthDb
        const authUserCodeQuery = query(
          collection(authDb, 'user'),
          where('userCode', '>=', search.toUpperCase()),
          where('userCode', '<=', search.toUpperCase() + '\uf8ff'),
          limit(10)
        );
        authQueries.push(getDocs(authUserCodeQuery));
        
        // code ile arama - AuthDb
        const authCodeQuery = query(
          collection(authDb, 'user'),
          where('code', '>=', search.toUpperCase()),
          where('code', '<=', search.toUpperCase() + '\uf8ff'),
          limit(10)
        );
        authQueries.push(getDocs(authCodeQuery));
        
        // AuthDb sorgularını çalıştır
        const authSnapshots = await Promise.all(authQueries);
        
        // AuthDb sonuçlarını ekle (sadece ChatDb'de olmayanlar)
        authSnapshots.forEach(snapshot => {
          snapshot.forEach(doc => {
            const userData = doc.data();
            
            // Bu email veya kod ChatDb'de varsa, bu kullanıcıyı ekleme
            const userEmail = userData.email?.toLowerCase();
            const userCode = userData.userCode?.toUpperCase() || userData.code?.toUpperCase();
            
            if ((!userEmail || !foundEmails.has(userEmail)) && 
                (!userCode || !foundCodes.has(userCode))) {
              userData.source = 'authDb';
              if (!results.has(doc.id)) {
                results.set(doc.id, { id: doc.id, ...userData });
              }
            }
          });
        });
        
        //console.log(`AuthDb'de ${results.size} kullanıcı bulundu`);
      } catch (authError) {
        //console.error("AuthDb'de arama yaparken hata:", authError);
      }
      
      const allResults = Array.from(results.values()).map(user => {
        return {
          ...user,
          searchTerm: search,
          isEmailSearch
        };
      });
      
      //console.log(`Toplam ${allResults.length} benzersiz kullanıcı bulundu`);
      return allResults;
    } catch (error) {
      //console.error("Kullanıcıları ararken hata:", error);
      return [];
    }
  },

  /**
   * Kullanıcı profil resmini günceller
   * @param {string} userId - Kullanıcı ID
   * @param {File} file - Profil resmi dosyası
   * @returns {Promise<string>} - Profil resmi URL'i
   */
  async updateProfilePhoto(userId, file) {
    try {
      if (!userId || !file) {
        //console.error("updateProfilePhoto: Geçersiz parametreler");
        throw new Error("Geçersiz parametreler");
      }
      
      // Dosya türünü kontrol et
      if (!file.type.startsWith('image/')) {
        throw new Error("Sadece resim dosyaları yüklenebilir");
      }
      
      //console.log(`Profil resmi yükleniyor: ${file.name} (${file.size} bytes)`);
      
      // Dosyayı Storage'a yükle
      const storageRef = ref(chatStorage, `profilePictures/${userId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      
      // Dosya URL'ini al
      const photoURL = await getDownloadURL(storageRef);
      
      // Kullanıcı profilini her iki veritabanında da güncelle
      // AuthDb güncelleme
      const authUserQuery = query(
        collection(authDb, 'user'),
        where('uid', '==', userId),
        limit(1)
      );
      
      const authUserSnapshot = await getDocs(authUserQuery);
      if (!authUserSnapshot.empty) {
        const authUserDoc = authUserSnapshot.docs[0];
        await updateDoc(authUserDoc.ref, {
          photoURL,
          updatedAt: serverTimestamp()
        });
      }
      
      // ChatDb güncelleme
      await updateDoc(doc(chatDb, 'users', userId), {
        photoURL,
        updatedAt: serverTimestamp()
      });
      
      // Auth kullanıcısını güncelle
      const user = auth.currentUser;
      if (user && user.uid === userId) {
        await updateProfile(user, { photoURL });
      }
      
      //console.log("Profil resmi başarıyla güncellendi:", photoURL);
      return photoURL;
    } catch (error) {
      //console.error('Profil resmi güncellenirken hata:', error);
      throw error;
    }
  },

  /**
   * Bir mesajı okundu olarak işaretle
   * @param {string} chatId - Sohbet ID
   * @param {string} messageId - Mesaj ID
   * @param {string} userId - Okuyan kullanıcı ID
   * @returns {Promise<boolean>} - İşlem başarılı mı?
   */
  async markMessageAsRead(chatId, messageId, userId) {
    try {
      //alert("markMessageAsRead çalışıyor");
      // Validate parameters
      if (!chatId || !messageId || !userId) {
        //console.error('Missing required parameters for markMessageAsRead:', { chatId, messageId, userId });
        return false;
      }

      //console.log(`Mesaj ${messageId} okundu olarak işaretleniyor (${userId})`);

      // Get the message document reference
      const messageRef = doc(chatDb, "chats", chatId, "messages", messageId);
      
      try {
        // Önce mesajı al ve readBy alanını kontrol et
        const messageSnap = await getDoc(messageRef);
        if (!messageSnap.exists()) {
          //console.warn(`Mesaj bulunamadı: ${chatId}/${messageId}`);
          return false;
        }
        
        const messageData = messageSnap.data();
        const readBy = messageData.readBy || [];
        
        // Bu kullanıcı zaten mesajı okuduysa, işlem yapmadan çık
        if (readBy.includes(userId)) {
          //console.log(`Mesaj ${messageId} zaten ${userId} tarafından okunmuş.`);
          return true;
        }
        
        // Kullanıcıyı readBy dizisine ekle
        await updateDoc(messageRef, {
          readBy: arrayUnion(userId)
        });
        
        //console.log(`Mesaj okundu olarak işaretlendi: ${messageId} (${userId})`);
        return true;
      } catch (docError) {
        //console.error(`Mesaj ${messageId} erişilirken veya güncellenirken hata:`, docError);
        return false;
      }
    } catch (error) {
      //console.error(`Mesaj ${messageId} okundu olarak işaretlenirken hata:`, error);
      return false;
    }
  },

  /**
   * Birden fazla mesajı okundu olarak işaretle
   * @param {string} chatId - Sohbet ID
   * @param {Array<string>} messageIds - Mesaj ID'leri dizisi
   * @param {string} userId - Okuyan kullanıcı ID
   * @returns {Promise<boolean>} - İşlem başarılı mı?
   */
  async markMessagesAsRead(chatId, messageIds, userId) {
    try {
      if (!chatId || !userId) {
        //console.error('markMessagesAsRead: Geçersiz parametreler:', { chatId, userId });
        return false;
      }
      
      // OPTIMIZASYON KRİTİK: Sadece unread count'u sıfırla, mesajları tek tek güncelleme!
      // Bu 20 yazma yerine sadece 1 yazma yapacak!
      try {
        const chatRef = doc(chatDb, 'chats', chatId);
        await updateDoc(chatRef, {
          [`unreadCount_${userId}`]: 0
        })
        // Sadece okuyan kullanıcı için; mesaj göndermeden sohbet açanlar da “aktif” görünsün
        await touchUserLastSeen(userId, { force: false })
        //console.log(`Unread count sıfırlandı: ${chatId} - ${userId}`);
        return true;
      } catch (error) {
        console.warn('Unread count sıfırlanırken hata:', error);
        return false;
      }
    } catch (error) {
      //console.error('Mesajlar okundu olarak işaretlenirken hata:', error);
      return false;
    }
  },

  /**
   * Uploads a file to storage and sends a message with it.
   * @param {string} chatId - The ID of the chat
   * @param {string} userId - The ID of the user sending the message
   * @param {File} file - The file to upload
   * @param {string} [type='file'] - The type of the file ('image', 'video', etc.)
   * @returns {Promise<Object>} - A promise that resolves with the message object
   */
  async uploadAndSendFile(chatId, userId, file, type = 'file') {
    try {
      if (!chatId || !userId || !file) throw new Error('Eksik parametre');
      const userProfile = await this.getUserProfile(userId);
      const maxSize = getMaxUploadSizeForProfile(userProfile);
      if (file.size > maxSize) throw new Error('Dosya büyük');
      const fileURL = await this.uploadFile(chatId, file);
      const additionalData = {
        fileName: file.name || 'file',
        fileSize: file.size,
        fileType: file.type,
        fileURL
      };
      let finalType = type;
      if (file.type.startsWith('image/')) finalType = 'image';
      else if (file.type.startsWith('video/')) finalType = 'video';
      else if (file.type.includes('pdf') || file.type.includes('doc') || file.type.includes('xls') || file.type.includes('ppt')) finalType = 'document';
      else finalType = 'file';
      //console.log('sendMessage çağrılıyor:', {chatId, userId, fileURL, finalType, additionalData});
      const msg = await this.sendMessage(chatId, userId, fileURL, finalType, additionalData);
      //console.log('sendMessage sonucu:', msg);
      return msg;
    } catch (error) {
      //console.error('Error uploading and sending file:', error);
      return undefined;
    }
  },

  // Daha eski mesajları yükle - pagination için
  async loadOlderMessages(chatId, lastMessage, limit = 20, shouldMarkAsRead = true, currentUserId = null) {
    try {
      if (!chatId || !lastMessage || !lastMessage.timestamp) {
        //console.error('loadOlderMessages: Geçersiz parametreler');
        return [];
      }

      if (shouldMarkAsRead && !currentUserId) {
        //console.warn('loadOlderMessages: currentUserId belirtilmeden mesajlar okundu olarak işaretlenemez');
        shouldMarkAsRead = false;
      }

      //console.log(`Daha eski mesajlar yükleniyor: ${chatId}, limit: ${limit}, timestamp: ${lastMessage.timestamp}`);
      
      // Son mesajdan daha eski mesajları getir
      const messagesQuery = query(
        collection(chatDb, `chats/${chatId}/messages`),
        orderBy('timestamp', 'desc'),
        startAfter(lastMessage.timestamp),
        limit(limit)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Mesajları doğru sırada döndür (en eski mesaj en üstte)
      const sortedMessages = messages.reverse();
      
      // Otomatik olarak mesajları okundu olarak işaretle
      if (shouldMarkAsRead && currentUserId && sortedMessages.length > 0) {
        // Sadece başkasının mesajlarını işaretle, kendi mesajlarını değil
        const unreadMessages = sortedMessages.filter(message => 
          message.senderId !== currentUserId && // Kendi mesajın değil
          (!message.readBy || !message.readBy.includes(currentUserId)) // Henüz okunmamış
        );
        
        if (unreadMessages.length > 0) {
          //console.log(`${unreadMessages.length} eski okunmamış mesaj işaretleniyor...`);
          
          // Sadece daha eski mesajların ID'lerini topla
          const messageIds = unreadMessages.map(m => m.id);
          
          // Okuma/yazma işlemini azaltmak için toplu işlem yap
          this.markMessagesAsRead(chatId, messageIds, currentUserId)
            .catch(err => console.warn('Eski mesajlar okundu olarak işaretlenirken hata:', err));
        }
      }
      
      return sortedMessages;
    } catch (error) {
      //console.error(`Eski mesajlar yüklenirken hata: ${chatId}`, error);
      return [];
    }
  },
};

// Yardımcı fonksiyon: Chat dokümanına kullanıcı detaylarını ekler
const getChatWithUserDetails = async (chatDoc) => {
  const chatData = chatDoc.data();
  const chatId = chatDoc.id;
  
  // Kullanıcı detayları zaten varsa, ek işlem yapmadan döndür
  if (chatData.userDetails) {
    return { id: chatId, ...chatData };
  }
  
  try {
    // Kullanıcı detayları yoksa, kullanıcı bilgilerini al
    if (chatData.isGroup) {
      // Grup sohbeti ise grup adını görüntüle
      return { 
        id: chatId, 
        ...chatData,
        name: chatData.groupName || 'Grup Sohbeti'
      };
    } else {
      // Özel sohbet ise kullanıcıların bilgilerini getir
      if (chatData.users && chatData.users.length >= 2) {
        const userDetails = {};
        
        // Her bir kullanıcı için bilgi topla
        for (const userId of chatData.users) {
          try {
            //console.log(`Chat ${chatId} için kullanıcı bilgisi alınıyor: ${userId}`);
            
            // userService'i kullanarak kullanıcı bilgilerini al - en kapsamlı şekilde arar
            const userProfileData = await userService.getUserProfile(userId);
            
            if (userProfileData) {
              // Kullanıcı bilgileri bulundu
              let displayName = userProfileData.displayName || '';
              
              // Farklı adlandırma kurallarını kontrol et
              if (!displayName && userProfileData.name) {
                if (userProfileData.lastname) {
                  displayName = `${userProfileData.name} ${userProfileData.lastname}`;
                } else {
                  displayName = userProfileData.name;
                }
              }
              
              // userCode kontrolü - birçok farklı alanı kontrol et
              const userCode = userProfileData.userCode || userProfileData.code || '';
              
              // userCode varsa, onu isimsiz kullanıcı yerine göster
              if (userCode && (!displayName || displayName === 'Kullanıcı')) {
                displayName = userCode;
              }
              
              userDetails[userId] = {
                displayName: displayName || userCode || userId.substring(0, 6),
                photoURL: userProfileData.photoURL || '',
                userCode: userCode || userId.substring(0, 6),
                status: userProfileData.status || 'Bilinmiyor',
                lastSeenAt: userProfileData.lastSeenAt || userProfileData.updatedAt || null,
                source: userProfileData.source || 'userService'
              };
              
              //console.log(`Chat ${chatId} için ${userId} kullanıcı bilgileri bulundu`);
            } else {
              // Kullanıcı bilgileri bulunamadı, varsayılan değerler kullan
              //console.log(`Chat ${chatId} için ${userId} kullanıcı bulunamadı - varsayılan kullanılıyor`);
              userDetails[userId] = {
                displayName: userId.substring(0, 6), // UserID'nin kısa formu
                photoURL: '',
                userCode: userId.substring(0, 6),
                status: 'Bilinmiyor',
                source: 'default'
              };
            }
          } catch (error) {
            //console.error(`${userId} için bilgi alınırken hata:`, error);
            userDetails[userId] = {
              displayName: userId.substring(0, 6),
              photoURL: '',
              userCode: userId.substring(0, 6),
              status: 'Bilinmiyor',
              source: 'error'
            };
          }
        }
        
        // Chat belgesini güncelle
        if (Object.keys(userDetails).length > 0) {
          try {
            await updateDoc(doc(chatDb, `chats/${chatId}`), { userDetails });
            //console.log(`Chat ${chatId} için kullanıcı detayları güncellendi`);
          } catch (error) {
            //console.error(`Chat ${chatId} için kullanıcı detayları güncellenemedi:`, error);
          }
        }
        
        return {
          id: chatId,
          ...chatData,
          userDetails
        };
      }
    }
  } catch (error) {
    //console.error(`Chat ${chatId} için kullanıcı detayları alınamadı:`, error);
  }
  
  // Hiçbir bilgi elde edilemezse orijinal veriyi döndür
  return { id: chatId, ...chatData };
};

// Sohbet Servisi
export const chatService = {
  /**
   * Yeni bir sohbet oluşturur
   * @param {object} chatData - Sohbet bilgileri
   * @returns {Promise<string>} - Sohbet ID
   */
  async createChat(chatData) {
    try {
      // Eğer basit kullanıcı listesi verilmişse, onu tam sohbet verisine dönüştür
      if (Array.isArray(chatData)) {
        chatData = {
          users: chatData,
          isGroup: false,
          createdAt: serverTimestamp(),
          lastMessage: null,
          lastMessageTimestamp: null
        };
      } else {
        // chatData bir obje ise, eksik alanları ekle
        chatData = {
          ...chatData,
          createdAt: serverTimestamp(),
          lastMessage: chatData.lastMessage || null,
          lastMessageTimestamp: chatData.lastMessageTimestamp || serverTimestamp()
        };
      }
      
      const chatRef = await addDoc(collection(chatDb, 'chats'), chatData);
      return chatRef.id;
    } catch (error) {
      //console.error("Sohbet oluşturulurken hata:", error);
      throw error;
    }
  },

  // Sohbetleri al
  getChats: async (uid) => {
    try {
      const q = query(
        collection(chatDb, 'chats'),
        where('users', 'array-contains', uid),
        orderBy('lastMessageTimestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const chats = [];
      
      // Her bir chat için kullanıcı detaylarını al
      for (const doc of querySnapshot.docs) {
        const chatWithDetails = await getChatWithUserDetails(doc);
        chats.push(chatWithDetails);
      }
      
      return chats;
    } catch (error) {
      //console.error("Sohbetler alınırken hata:", error);
      throw error;
    }
  },

  // Sohbetleri dinle - optimize edilmiş versiyon
  async listenToChats(userId, callback) {
    try {
      if (!userId) {
        //console.error('listenToChats: userId parametresi gerekli');
        callback([]);
        return () => {};
      }

      //console.log(`Sohbetler dinleniyor: ${userId}`);
      const chatsRef = collection(chatDb, "chats");
      
      // Kullanıcının olduğu sohbetler
      const userChatsQuery = query(
        chatsRef,
        where("users", "array-contains", userId),
        orderBy("lastMessageTimestamp", "desc"),
        limit(USER_CHATS_SNAPSHOT_LIMIT)
      );
      
      // Duyuru grupları - herkesin görebileceği announcement'lar (users array'i yok)
      // OPTIMIZASYON: Güvenlik limiti. Duyuru grubu sayısı beklenmedik şekilde artarsa
      // her açılış/değişiklikte hepsinin okunmasını engeller. Pratikte az sayıda olduğundan
      // davranışı etkilemez ama okuma maliyetine üst sınır koyar.
      const announcementChatsQuery = query(
        chatsRef,
        where("isAnnouncement", "==", true),
        limit(50)
        // users kontrolü yok - herkes görebilir
      );
      

      // Önceki dinleyicileri kaldır
      if (this.unsubscribeChats) {
        this.unsubscribeChats();
      }
      if (this.unsubscribeAnnouncements) {
        this.unsubscribeAnnouncements();
      }

      // OPTIMIZASYON: Her iki query için ayrı cache
      let userChatsData = [];
      let announcementChatsData = [];
      
      // Kullanıcı sohbetlerini dinle
      this.unsubscribeChats = onSnapshot(userChatsQuery, async (snapshot) => {
        const chatPromises = snapshot.docs.map(async (doc) => {
          const docData = doc.data()
          
          // Arşivlenmiş sohbetleri filtrele
          const archivedBy = docData.archivedBy || []
          if (archivedBy.includes(userId)) {
            return null // Bu kullanıcı arşivlemiş, gösterme
          }
          
          const chatData = {
            id: doc.id,
            lastMessage: docData.lastMessage,
            lastMessageTimestamp: docData.lastMessageTimestamp,
            isGroup: docData.isGroup || false,
            groupName: docData.groupName,
            groupPhotoURL: docData.groupPhotoURL,
            users: docData.users || [],
            userDetails: docData.userDetails || {},
            isAnnouncement: docData.isAnnouncement || false,
            // OPTIMIZASYON: Chat document'ında saklanan unread count'u kullan
            unreadCount: docData[`unreadCount_${userId}`] || 0
          };
          return chatData;
        });
        userChatsData = (await Promise.all(chatPromises)).filter(chat => chat !== null);
        
        // Her iki listeyi birleştir ve callback çağır
        mergeChatLists();
      }, (error) => {
        //console.error("Kullanıcı sohbetleri dinlerken hata:", error);
      });
      
      // OPTIMIZASYON KRİTİK: Announcement gruplarını da onSnapshot ile dinle (getDocs DEĞİL!)
      this.unsubscribeAnnouncements = onSnapshot(announcementChatsQuery, async (snapshot) => {
        const announcementPromises = snapshot.docs.map(async (doc) => {
          const docData = doc.data();
          const chatData = {
            id: doc.id,
            lastMessage: docData.lastMessage,
            lastMessageTimestamp: docData.lastMessageTimestamp,
            isGroup: docData.isGroup || false,
            groupName: docData.groupName,
            groupPhotoURL: docData.groupPhotoURL,
            users: docData.users || [],
            userDetails: docData.userDetails || {},
            isAnnouncement: docData.isAnnouncement || false,
            // OPTIMIZASYON: Chat document'ında saklanan unread count'u kullan
            unreadCount: docData[`unreadCount_${userId}`] || 0
          };
          return chatData;
        });
        announcementChatsData = (await Promise.all(announcementPromises)).filter(Boolean);
        
        // Her iki listeyi birleştir ve callback çağır
        mergeChatLists();
      }, (error) => {
        console.error("Announcement grupları dinlerken hata:", error);
      });
      
      // İki listeyi birleştir ve callback çağır
      function mergeChatLists() {
        // Duplicate kontrolü için Set kullan
        const chatIds = new Set();
        let allChats = [];
        
        // Önce kullanıcı sohbetlerini ekle (announcement olmayanlar)
        for (const chat of userChatsData) {
          if (!chat.isAnnouncement && !chatIds.has(chat.id)) {
            chatIds.add(chat.id);
            allChats.push(chat);
          }
        }
        
        // Sonra announcement gruplarını ekle (herkesin görebileceği)
        for (const chat of announcementChatsData) {
          if (!chatIds.has(chat.id)) {
            chatIds.add(chat.id);
            allChats.push(chat);
          }
        }
        
        // Son mesaj zamanına göre sırala
        allChats.sort((a, b) => {
          if (!a.lastMessageTimestamp) return 1;
          if (!b.lastMessageTimestamp) return -1;
          const timeA = a.lastMessageTimestamp?.seconds ?
            a.lastMessageTimestamp.seconds * 1000 + (a.lastMessageTimestamp.nanoseconds || 0) / 1000000 : 0;
          const timeB = b.lastMessageTimestamp?.seconds ?
            b.lastMessageTimestamp.seconds * 1000 + (b.lastMessageTimestamp.nanoseconds || 0) / 1000000 : 0;
          return timeB - timeA;
        });
        
        callback(allChats);
      }

      return () => {
        if (this.unsubscribeChats) {
          this.unsubscribeChats();
        }
        if (this.unsubscribeAnnouncements) {
          this.unsubscribeAnnouncements();
        }
      };
    } catch (error) {
      //console.error('Sohbet dinleyicisi kurulurken hata:', error);
      callback([]);
      return () => {};
    }
  },

  /**
   * ChatDb users/{uid} belgesini dinler (lastSeenAt canlı güncellenir; sohbet listesi noktası için).
   * @param {string} uid
   * @param {(data: object | null) => void} callback
   * @returns {() => void} unsubscribe
   */
  subscribeChatUserLastSeen (uid, callback) {
    if (!uid || typeof callback !== 'function') {
      return () => {}
    }
    const userRef = doc(chatDb, 'users', uid)
    return onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          callback(null)
          return
        }
        callback(snap.data())
      },
      (err) => {
        console.warn('Kullanıcı presence dinlenemedi:', uid, err)
        callback(null)
      }
    )
  },
  
  // Sohbet kullanıcı detaylarını getir - yeni fonksiyon
  async getChatUserDetails(chatId) {
    try {
      if (!chatId) {
        //console.error('getChatUserDetails: chatId parametresi gerekli');
        return null;
      }
      
      //console.log(`Sohbet kullanıcı detayları getiriliyor: ${chatId}`);
      const chatDoc = await getDoc(doc(chatDb, 'chats', chatId));
      
      if (!chatDoc.exists()) {
        //console.error(`Chat bulunamadı: ${chatId}`);
        return null;
      }
      
      // Kullanıcı detaylarını doldur
      const chatWithDetails = await getChatWithUserDetails(chatDoc);
      return chatWithDetails.userDetails || null;
    } catch (error) {
      //console.error(`Sohbet kullanıcı detayları alınırken hata: ${chatId}`, error);
      return null;
    }
  },

  /**
   * Sends a message to a chat.
   * @param {string} chatId - The ID of the chat.
   * @param {string} userId - The ID of the user sending the message.
   * @param {string} content - The content of the message.
   * @param {string} type - The type of the message.
   * @param {Object} additionalData - Additional data for the message.
   * @returns {Promise<Object>} - A promise that resolves with the message ID.
   */
  async sendMessage(chatId, userId, content, type = 'text', additionalData = {}) {
    try {
      //console.log('sendMessage başı:', {chatId, userId, content, type, additionalData});
      // Validate parameters
      if (!chatId || !userId || !content) {
        //console.error('sendMessage için gerekli parametreler eksik');
        throw new Error('sendMessage için gerekli parametreler eksik');
      }

      // OPTIMIZASYON: Sohbet belgesini SADECE 1 kez oku (önceden 2 kez okunuyordu:
      // biri duyuru kontrolü, biri unread güncellemesi için). Aynı veriyi ikisinde de kullan.
      const chatRef = doc(chatDb, "chats", chatId);
      const chatDoc = await getDoc(chatRef);
      const chatDataForSend = chatDoc.exists() ? chatDoc.data() : null;

      // Duyuru grubu kontrolü - sadece control/admin yazabilir
      if (chatDataForSend && chatDataForSend.isAnnouncement) {
        // Kullanıcı yetkisini kontrol et
        const userProfile = await userService.getUserProfile(userId);
        const isAdminOrControl = userProfile && (
          userProfile.userRole === 'control' || 
          userProfile.userRole === 'admin' ||
          userProfile.role === 'control' ||
          userProfile.role === 'admin'
        );
        
        if (!isAdminOrControl) {
          throw new Error('Duyuru gruplarına sadece control ve admin yetkisindeki kullanıcılar mesaj gönderebilir');
        }
      }

      //console.log(`Mesaj gönderiliyor: Chat(${chatId}), User(${userId}), Type(${type})`);

      // Şu anki zaman bilgisi ve sayısal timestamp
      const now = new Date();
      const numericalTimestamp = now.getTime();
      const currentTimestamp = serverTimestamp();

      // Prepare message data
      const messageData = {
        content,
        type,
        senderId: userId,
        timestamp: currentTimestamp,
        readBy: [userId], // Sender has read the message by default
        ...additionalData
      };

      // Add message to messages collection
      const messagesRef = collection(chatDb, "chats", chatId, "messages");
      const messageRef = await addDoc(messagesRef, messageData);
      //console.log(`Mesaj kaydedildi: ${messageRef.id}`);

      try {
        // OPTIMIZASYON: Yukarıda okunan chat verisini yeniden kullan (ekstra getDoc yok).
        if (chatDataForSend) {
          const users = chatDataForSend.users || [];
          
          // Update lastMessage in chat document with known values
          const lastMessageUpdate = {
            lastMessage: {
              content: content || '',
              senderId: userId,
              type: type || 'text',
              timestamp: currentTimestamp
            },
            lastMessageTimestamp: currentTimestamp,
            lastMessageSenderId: userId,
            numericalTimestamp: numericalTimestamp,
            lastUpdated: currentTimestamp
          };
          
          // OPTIMIZASYON: Atomik increment kullan (read-modify-write yerine).
          // Hem eşzamanlı mesajlarda sayaç doğru kalır hem de ekstra okuma gerekmez.
          users.forEach(uid => {
            if (uid !== userId) {
              lastMessageUpdate[`unreadCount_${uid}`] = increment(1);
            }
          });
          
          // Update the chat document with lastMessage and unread counts
          await updateDoc(chatRef, lastMessageUpdate);
          //console.log(`Sohbet son mesaj bilgisi ve unread count güncellendi: ${chatId}`);
        }
      } catch (chatUpdateError) {
        //console.error("Sohbet belgesi güncellenirken hata:", chatUpdateError);
        // Mesaj gönderildiği için hatayı yutabiliriz
      }

      try {
        // Turn off typing indicator for this user after sending a message
        await this.updateTypingStatus(chatId, userId, false);
      } catch (typingError) {
        //console.warn("Yazıyor durumu kapatılırken hata:", typingError);
      }

      // Mesaj = uygulama aktivitesi; yalnızca presence aralığına bağlı kalınırsa karşı taraf
      // "Son görülme bilinmiyor" görebilir (özellikle ilk açılış / arka plan sekmesi).
      await touchUserLastSeen(userId, { force: true })

      //console.log('sendMessage sonu:', {id: messageRef.id, ...messageData});
      return {
        id: messageRef.id,
        ...messageData,
        timestamp: { seconds: Math.floor(numericalTimestamp / 1000), nanoseconds: 0 }
      };
    } catch (error) {
      //console.error('Mesaj gönderilirken hata:', error);
      throw error;
    }
  },

  /**
   * Mesajı düzenle
   * @param {string} chatId - Sohbet ID
   * @param {string} messageId - Mesaj ID
   * @param {string} newContent - Yeni içerik
   * @param {string} userId - Düzenleyen kullanıcı ID
   * @returns {Promise<boolean>}
   */
  async editMessage(chatId, messageId, newContent, userId) {
    try {
      const messageRef = doc(chatDb, 'chats', chatId, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) {
        throw new Error('Mesaj bulunamadı');
      }
      
      const messageData = messageDoc.data();
      
      // Sadece mesajı gönderen düzenleyebilir
      if (messageData.senderId !== userId) {
        throw new Error('Bu mesajı düzenleme yetkiniz yok');
      }
      
      // Mesajı güncelle
      await updateDoc(messageRef, {
        content: newContent,
        edited: true,
        editedAt: serverTimestamp()
      });
      
      // Son mesaj ise chat document'ını da güncelle
      const chatRef = doc(chatDb, 'chats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (chatDoc.exists()) {
        const chatData = chatDoc.data();
        if (chatData.lastMessage && chatData.lastMessage.senderId === userId) {
          await updateDoc(chatRef, {
            'lastMessage.content': newContent
          });
        }
      }
      
      return true;
    } catch (error) {
      console.error('Mesaj düzenlenirken hata:', error);
      throw error;
    }
  },

  /**
   * Mesajı sil (soft delete - sadece içerik değiştir)
   * @param {string} chatId - Sohbet ID
   * @param {string} messageId - Mesaj ID
   * @param {string} userId - Silen kullanıcı ID
   * @returns {Promise<boolean>}
   */
  async deleteMessage(chatId, messageId, userId) {
    try {
      const messageRef = doc(chatDb, 'chats', chatId, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) {
        throw new Error('Mesaj bulunamadı');
      }
      
      const messageData = messageDoc.data();
      
      // Kullanıcı yetkisini kontrol et
      const userProfile = await userService.getUserProfile(userId);
      const isAdminOrControl = userProfile && (userProfile.userRole === 'admin' || userProfile.userRole === 'control');
      
      // Sadece mesajı gönderen veya admin/control silebilir
      if (messageData.senderId !== userId && !isAdminOrControl) {
        throw new Error('Bu mesajı silme yetkiniz yok');
      }
      
      // Mesajı soft delete yap
      await updateDoc(messageRef, {
        content: 'Bu mesaj silindi',
        deleted: true,
        deletedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Mesaj silinirken hata:', error);
      throw error;
    }
  },

  // Mesajları dinle - optimize edilmiş versiyon (sadece son mesajlar)
  listenToMessages: (chatId, callback, limit = 20, startAfter = null, shouldMarkAsRead = true, currentUserId = null) => {
    try {
      if (!chatId) {
        //console.error('listenToMessages: chatId parametresi gerekli');
        callback([]);
        return () => {};
      }

      if (shouldMarkAsRead && !currentUserId) {
        //console.warn('listenToMessages: currentUserId belirtilmeden mesajlar okundu olarak işaretlenemez');
        shouldMarkAsRead = false;
      }

      //console.log(`Mesajlar dinleniyor: ${chatId}, limit: ${limit}, currentUserId: ${currentUserId || 'belirtilmedi'}`);
      
      // Sorguyu oluştur - son mesajları alacak şekilde (en yakın tarihten en eskiye)
      let messagesQuery = query(
        collection(chatDb, `chats/${chatId}/messages`),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );
      
      // Eğer startAfter belirtilmişse, sorguya pagination ekle
      if (startAfter) {
        messagesQuery = query(messagesQuery, startAfter(startAfter));
      }
      
      // Dinleyiciyi ayarla
      const unsubscribe = onSnapshot(messagesQuery, (querySnapshot) => {
        const messages = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Mesajları doğru sırada döndür (en eski mesaj en üstte)
        const sortedMessages = messages.reverse();
        
        // Otomatik olarak mesajları okundu olarak işaretle
        if (shouldMarkAsRead && currentUserId) {
          // Sadece başkasının mesajlarını işaretle, kendi mesajlarını değil
          const unreadMessages = sortedMessages.filter(message => 
            message.senderId !== currentUserId && // Kendi mesajın değil
            (!message.readBy || !message.readBy.includes(currentUserId)) // Henüz okunmamış
          );
          
          if (unreadMessages.length > 0) {
            //console.log(`${unreadMessages.length} okunmamış mesaj işaretleniyor...`);
            
            // Mesajları toplu olarak işaretle
            chatService.markMessagesAsRead(
              chatId, 
              unreadMessages.map(m => m.id), 
              currentUserId
            ).catch(err => {
              //console.warn('Mesajlar okundu olarak işaretlenirken hata:', err);
            });
          }
        }
        
        callback(sortedMessages);
      }, (error) => {
        //console.error(`Mesajlar dinlenirken hata: ${chatId}`, error);
        callback([]);
      });
      
      return unsubscribe;
    } catch (error) {
      //console.error(`Mesaj dinleyicisi oluşturulurken hata: ${chatId}`, error);
      callback([]);
      return () => {};
    }
  },

  // Sohbet seçilince mesajları yükle
  async loadChatMessages(chatId, limit = 20, shouldMarkAsRead = true, currentUserId = null) {
    try {
      if (!chatId) {
        //console.error('loadChatMessages: chatId parametresi gerekli');
        return [];
      }

      if (shouldMarkAsRead && !currentUserId) {
        //console.warn('loadChatMessages: currentUserId belirtilmeden mesajlar okundu olarak işaretlenemez');
        shouldMarkAsRead = false;
      }

      //console.log(`Sohbet mesajları yükleniyor: ${chatId}, limit: ${limit}, currentUserId: ${currentUserId || 'belirtilmedi'}`);
      
      // Son mesajları getir
      const messagesQuery = query(
        collection(chatDb, `chats/${chatId}/messages`),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Mesajları doğru sırada döndür (en eski mesaj en üstte)
      const sortedMessages = messages.reverse();
      
      // Otomatik olarak mesajları okundu olarak işaretle
      if (shouldMarkAsRead && currentUserId) {
        // Sadece başkasının mesajlarını işaretle, kendi mesajlarını değil
        const unreadMessages = sortedMessages.filter(message => 
          message.senderId !== currentUserId && // Kendi mesajın değil
          (!message.readBy || !message.readBy.includes(currentUserId)) // Henüz okunmamış
        );
        
        if (unreadMessages.length > 0) {
          //console.log(`${unreadMessages.length} okunmamış mesaj işaretleniyor...`);
          
          // Mesajları toplu olarak işaretle
          try {
            await chatService.markMessagesAsRead(
              chatId, 
              unreadMessages.map(m => m.id), 
              currentUserId
            );
          } catch (err) {
            //console.warn('Mesajlar okundu olarak işaretlenirken hata:', err);
          }
        }
      }
      
      return sortedMessages;
    } catch (error) {
      //console.error(`Sohbet mesajları yüklenirken hata: ${chatId}`, error);
      return [];
    }
  },
  
  /**
   * Yazıyor durumunu güncelle - 5 saniye içinde otomatik sıfırlama ile
   * @param {string} chatId - Sohbet ID
   * @param {string} userId - Kullanıcı ID
   * @param {boolean} isTyping - Yazıyor durumu
   * @returns {Promise<void>}
   */
  async updateTypingStatus(chatId, userId, isTyping) {
    try {
      // Validate parameters
      if (!chatId || !userId) {
        //console.error('Missing required parameters for updateTypingStatus');
        throw new Error('Missing required parameters for updateTypingStatus');
      }
      
      // Eğer önceki zamanlayıcı varsa temizle
      if (this.typingTimeouts && this.typingTimeouts[userId]) {
        clearTimeout(this.typingTimeouts[userId]);
        delete this.typingTimeouts[userId];
      }

      // Get the typing document reference
      const typingRef = doc(chatDb, "chats", chatId, "typing", userId);
      
      if (isTyping) {
        // Update or create the typing document
        await setDoc(typingRef, {
          userId,
          timestamp: serverTimestamp()
        });
        
        // 5 saniye sonra otomatik olarak temizle (aşırı yazma/okuma önlemi)
        if (!this.typingTimeouts) this.typingTimeouts = {};
        this.typingTimeouts[userId] = setTimeout(async () => {
          try {
            // 5 saniye geçti, yazıyor durumunu kaldır
            await deleteDoc(typingRef);
            delete this.typingTimeouts[userId];
          } catch (e) {
            //console.warn('Yazıyor durumu otomatik temizleme hatası:', e);
          }
        }, 5000);
      } else {
        // Remove the typing document
        await deleteDoc(typingRef);
      }
    } catch (error) {
      //console.error('Error updating typing status:', error);
      // Hatayı yut, kritik değil
    }
  },

  /**
   * Yazıyor durumunu dinle (geriye doğru uyumlu)
   * @param {string} chatId - Sohbet ID
   * @param {function} callback - Geri çağırma fonksiyonu
   * @returns {function} - Temizleme fonksiyonu
   */
  listenToTypingStatus: (chatId, callback) => {
    try {
      if (!chatId || !callback) {
        //console.error('Missing required parameters for listenToTypingStatus');
        return () => {};
      }

      // OPTIMIZASYON KRİTİK: 5sn'de bir getDocs polling YERİNE onSnapshot.
      // Polling: sohbet açıkken sürekli okuma (saatte ~720 okuma/sohbet).
      // onSnapshot: sadece biri yazmaya başlayınca/bırakınca okuma (event başına ~1-2).
      const TYPING_TTL_MS = 10000;
      const typingColRef = collection(chatDb, "chats", chatId, "typing");

      let lastDocs = [];
      let lastEmittedKey = null;
      let staleTimeoutId = null;

      // Snapshot verisinden güncel "yazıyor" haritasını hesapla ve değişiklik varsa yayınla.
      // Ayrıca en yakın süresi dolacak kayıt için tek seferlik timeout kurar; böylece
      // silme event'i gelmese bile eski gösterge kendini temizler (polling maliyeti olmadan).
      const computeAndEmit = () => {
        const now = Date.now();
        const typingUsers = {};

        lastDocs.forEach(({ id, timestampMs }) => {
          // timestampMs === 0: serverTimestamp henüz yazılmamış (yeni oluşturuldu) → yazıyor say
          if (timestampMs === 0 || now - timestampMs < TYPING_TTL_MS) {
            typingUsers[id] = true;
          }
        });

        const key = Object.keys(typingUsers).sort().join(',');
        if (key !== lastEmittedKey) {
          lastEmittedKey = key;
          callback(typingUsers);
        }

        if (staleTimeoutId) {
          clearTimeout(staleTimeoutId);
          staleTimeoutId = null;
        }
        const remainingTimes = lastDocs
          .map(d => d.timestampMs)
          .filter(t => t > 0 && now - t < TYPING_TTL_MS)
          .map(t => TYPING_TTL_MS - (now - t));
        if (remainingTimes.length > 0) {
          staleTimeoutId = setTimeout(computeAndEmit, Math.min(...remainingTimes) + 50);
        }
      };

      const unsubscribe = onSnapshot(typingColRef, (snapshot) => {
        lastDocs = snapshot.docs.map((doc) => {
          const data = doc.data();
          const ts = data.timestamp?.toDate?.();
          return { id: doc.id, timestampMs: ts ? ts.getTime() : 0 };
        });
        computeAndEmit();
      }, (error) => {
        //console.warn('Yazıyor durumu dinlenirken hata:', error);
      });

      // Eski API ile uyumlu: temizleme fonksiyonunu doğrudan döndür
      return () => {
        if (staleTimeoutId) {
          clearTimeout(staleTimeoutId);
          staleTimeoutId = null;
        }
        unsubscribe();
      };
    } catch (error) {
      //console.error('Error setting up typing status listener:', error);
      return () => {}; // Boş temizleme fonksiyonu
    }
  },

  /**
   * Uploads a file to storage and sends a message with it.
   * @param {string} chatId - The ID of the chat
   * @param {string} chatId - Sohbet ID
   * @param {File} file - Yüklenecek dosya
   * @param {Function} progressCallback - İlerleme durumu callback
   * @returns {Promise<string>} - Dosya URL'i
   */
  uploadFile: async (chatId, file, progressCallback) => {
    try {
      //console.log('uploadFile başı:', { chatId, fileName: file?.name, fileType: file?.type, fileSize: file?.size });
      if (!chatId || !file) {
        throw new Error('Missing required parameters for uploadFile');
      }
      // Create a unique filename
      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const fileRef = ref(chatStorage, `chatFiles/${chatId}/${fileName}`);
      // Upload with progress tracking if callback provided
      if (typeof progressCallback === 'function') {
        const uploadTask = uploadBytesResumable(fileRef, file);
        uploadTask.on('state_changed', 
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            progressCallback(progress);
          },
          (error) => {
            //console.error('Upload error:', error);
            throw error;
          }
        );
        // Wait for completion
        await uploadTask;
      } else {
        // Simple upload without progress tracking
        await uploadBytes(fileRef, file);
      }
      // Get the download URL
      const fileURL = await getDownloadURL(fileRef);
      //console.log('uploadFile sonucu fileURL:', fileURL);
      return fileURL;
    } catch (error) {
      //console.error('uploadFile hatası:', { chatId, fileName: file?.name, error });
      throw error;
    }
  },

  // Sohbet detaylarını getir - OPTIMIZASYON: Cache ile
  getChatDetails: async (chatId) => {
    try {
      if (!chatId) {
        //console.error('getChatDetails: chatId parametresi gerekli');
        throw new Error("Sohbet ID gerekli");
      }
      
      // OPTIMIZASYON: Cache'den kontrol et
      const cached = chatMetadataCache.get(chatId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        //console.log(`Cache'den chat metadata döndürüldü: ${chatId}`);
        return cached.data;
      }
      
      //console.log(`Chat detayları alınıyor: ${chatId}`);
      const chatDoc = await getDoc(doc(chatDb, 'chats', chatId));
      
      if (!chatDoc.exists()) {
        //console.error(`Chat bulunamadı: ${chatId}`);
        throw new Error("Sohbet bulunamadı");
      }
      
      // Temel chat verisini al
      const chatData = await getChatWithUserDetails(chatDoc);
      
      // Katılımcılar listesini oluştur
      const participants = chatData.users || [];
      
      let result;
      // Sohbet türüne göre formatla
      if (chatData.isGroup) {
        // Grup sohbeti
        result = {
          id: chatId,
          name: chatData.groupName || 'Grup Sohbeti',
          isGroup: true,
          isAnnouncement: chatData.isAnnouncement || false,
          participants,
          photoURL: chatData.groupPhotoURL || '',
          createdBy: chatData.createdBy || '',
          createdAt: chatData.createdAt || null,
          userDetails: chatData.userDetails || {}
        };
      } else {
        // Özel sohbet - basit bir yapı döndür
        result = {
          id: chatId,
          isGroup: false,
          participants,
          userDetails: chatData.userDetails || {}
        };
      }
      
      // OPTIMIZASYON: Sonucu cache'e kaydet
      chatMetadataCache.set(chatId, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      //console.error("Chat detayları alınırken hata:", error);
      throw error;
    }
  },
  
  // Duplicate sohbetleri temizle (aynı iki kullanıcı arasındaki birden fazla sohbet)
  async cleanupDuplicateChats() {
    try {
      // Tüm özel sohbetleri al - field kontrolü olmadan
      const q = query(
        collection(chatDb, 'chats')
      );
      
      const snapshot = await getDocs(q);
      const chatGroups = new Map(); // userId1_userId2 -> [chat1, chat2, ...]
      
      // Sohbetleri grupla
      snapshot.forEach(doc => {
        const chatData = doc.data();
        
        // Özel sohbet kontrolü: 2 kullanıcı ve grup/duyuru değil
        if (chatData.users && chatData.users.length === 2 && 
            !chatData.isGroup && !chatData.isAnnouncement) {
          const [user1, user2] = chatData.users.sort(); // Alfabetik sırala
          const key = `${user1}_${user2}`;
          
          if (!chatGroups.has(key)) {
            chatGroups.set(key, []);
          }
          chatGroups.get(key).push({ id: doc.id, ...chatData });
        }
      });
      
      // Her grup için duplicate kontrolü yap - SADECE LOG
      let duplicateCount = 0;
      for (const [key, chats] of chatGroups) {
        if (chats.length > 1) {
          // En yeni sohbeti tut, diğerlerini sil
          const sortedChats = chats.sort((a, b) => {
            const timeA = a.lastMessageTimestamp?.seconds || 0;
            const timeB = b.lastMessageTimestamp?.seconds || 0;
            return timeB - timeA; // En yeni en üstte
          });
          
          const keepChat = sortedChats[0]; // En yeni sohbet
          const deleteChats = sortedChats.slice(1); // Silinecek sohbetler
          
          // GÜVENLİ MOD: Sadece log, silme!
          duplicateCount += deleteChats.length;
        }
      }
      
      return 0; // Hiç silme
    } catch (error) {
      console.error("Duplicate sohbetler kontrol edilirken hata:", error);
      return 0;
    }
  },
  
  // Mevcut özel sohbeti bul (helper fonksiyon)
  async findExistingPrivateChat(userId1, userId2) {
    try {
      // Her iki kullanıcıyı da içeren sohbetleri ara - field kontrolü olmadan
      const q1 = query(
        collection(chatDb, 'chats'),
        where('users', 'array-contains', userId1)
      );
      
      const q2 = query(
        collection(chatDb, 'chats'),
        where('users', 'array-contains', userId2)
      );
      
      // Her iki query'yi paralel olarak çalıştır
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(q1),
        getDocs(q2)
      ]);
      
      // İlk query'den sonuçları kontrol et
      for (const doc of snapshot1.docs) {
        const chatData = doc.data();
        
        // Özel sohbet kontrolü: 2 kullanıcı ve grup/duyuru değil
        if (chatData.users && chatData.users.length === 2 && 
            chatData.users.includes(userId2) &&
            !chatData.isGroup && !chatData.isAnnouncement) {
          return { id: doc.id, ...chatData };
        }
      }
      
      // İkinci query'den sonuçları kontrol et
      for (const doc of snapshot2.docs) {
        const chatData = doc.data();
        
        // Özel sohbet kontrolü: 2 kullanıcı ve grup/duyuru değil
        if (chatData.users && chatData.users.length === 2 && 
            chatData.users.includes(userId1) &&
            !chatData.isGroup && !chatData.isAnnouncement) {
          return { id: doc.id, ...chatData };
        }
      }
      
      return null; // Sohbet bulunamadı
    } catch (error) {
      console.error("Mevcut sohbet aranırken hata:", error);
      return null;
    }
  },
  
  // Özel sohbet bul veya oluştur
  findOrCreatePrivateChat: async (userId1, userId2) => {
    try {
      // Önce iki kullanıcı arasında mevcut bir sohbet olup olmadığını kontrol et
      //console.log(`Özel sohbet aranıyor: ${userId1} ve ${userId2}`);
      
      // Mevcut sohbeti bul
      const existingChat = await chatService.findExistingPrivateChat(userId1, userId2);
      
      // Mevcut sohbet varsa, arşivden çıkar ve döndür
      if (existingChat) {
        // Eğer sohbet arşivlenmişse, arşivden çıkar
        const archivedBy = existingChat.archivedBy || [];
        if (archivedBy.includes(userId1)) {
          try {
            const chatRef = doc(chatDb, 'chats', existingChat.id);
            await updateDoc(chatRef, {
              archivedBy: arrayRemove(userId1)
            });
            
            // Arşivden çıkarılan sohbet için eski mesajları yükleme flag'i ekle
            // Bu flag ChatWindow.js'de kontrol edilecek
            existingChat._loadArchivedMessages = true;
          } catch (error) {
            console.warn('Sohbet arşivden çıkarılırken hata:', error);
          }
        }
        return existingChat.id;
      }
      
      // Mevcut sohbet yoksa, yeni bir sohbet oluştur
      
      // Son bir kez daha kontrol et - race condition önleme
      const finalCheck = await chatService.findExistingPrivateChat(userId1, userId2);
      if (finalCheck) {
        return finalCheck.id;
      }
      
      // Kullanıcı bilgilerini al
      const [user1, user2] = await Promise.all([
        userService.getUserProfile(userId1),
        userService.getUserProfile(userId2)
      ]);
      
      // Kullanıcı detaylarını hazırla
      const userDetails = {
        [userId1]: {
          displayName: user1?.displayName || userId1,
          photoURL: user1?.photoURL || '',
          userCode: user1?.userCode || user1?.code || '',
          status: user1?.status || 'Çevrimiçi',
          lastSeenAt: user1?.lastSeenAt || user1?.updatedAt || null
        },
        [userId2]: {
          displayName: user2?.displayName || userId2,
          photoURL: user2?.photoURL || '',
          userCode: user2?.userCode || user2?.code || '',
          status: user2?.status || 'Çevrimiçi',
          lastSeenAt: user2?.lastSeenAt || user2?.updatedAt || null
        }
      };
      
      // Chat meta verileri
      const chatData = {
        users: [userId1, userId2],
        isPrivate: true,
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTimestamp: serverTimestamp(),
        userDetails
      };
      
      const chatRef = await addDoc(collection(chatDb, 'chats'), chatData);
      
      return chatRef.id;
    } catch (error) {
      //console.error("Özel sohbet bulunurken veya oluşturulurken hata:", error);
      throw error;
    }
  },
  
  // Sohbet mesajlarını dinle - OPTIMIZASYON: Pagination ile (son 20 mesaj)
  listenToChatMessages: (chatId, callback, messageLimit = 20) => {
    try {
      // OPTIMIZASYON: Son N mesajı dinle (maliyeti düşürür - 50'den 20'ye düşürüldü)
      const q = query(
        collection(chatDb, `chats/${chatId}/messages`),
        orderBy('timestamp', 'desc'),
        limit(messageLimit)
      );
      
      return onSnapshot(q, (querySnapshot) => {
        // Mesajları ters çevir (en eski en üstte olacak şekilde)
        const messages = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .reverse();
        callback(messages);
      });
    } catch (error) {
      //console.error("Mesajlar dinlenirken hata:", error);
      throw error;
    }
  },
  
  // Daha eski mesajları yükle - pagination için
  async loadOlderMessages(chatId, oldestMessage, messageLimit = 20) {
    try {
      if (!chatId || !oldestMessage || !oldestMessage.timestamp) {
        return [];
      }
      
      // En eski mesajdan daha eski mesajları getir
      const q = query(
        collection(chatDb, `chats/${chatId}/messages`),
        orderBy('timestamp', 'desc'),
        startAfter(oldestMessage.timestamp),
        limit(messageLimit)
      );
      
      const querySnapshot = await getDocs(q);
      const messages = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .reverse();
      
      return messages;
    } catch (error) {
      console.error('Eski mesajlar yüklenirken hata:', error);
      return [];
    }
  },

  // Bir mesajı okundu olarak işaretle
  markMessageAsRead: async (chatId, messageId, userId) => {
    return userService.markMessageAsRead(chatId, messageId, userId);
  },

  // Birden fazla mesajı okundu olarak işaretle
  markMessagesAsRead: async (chatId, messageIds, userId) => {
    return userService.markMessagesAsRead(chatId, messageIds, userId);
  },

  /**
   * Bir grup sohbetine kullanıcı(lar) ekler
   * @param {string} chatId - Grup sohbetinin ID'si
   * @param {Array<string>} userIds - Eklenecek kullanıcı ID'leri
   * @returns {Promise<void>}
   */
  async addUsersToGroup(chatId, userIds) {
    try {
      if (!chatId || !Array.isArray(userIds) || userIds.length === 0) {
        throw new Error('Geçersiz parametreler: chatId ve userIds gereklidir.');
      }
      // Sohbeti getir
      const chatRef = doc(chatDb, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      if (!chatSnap.exists()) {
        throw new Error('Sohbet bulunamadı.');
      }
      const chatData = chatSnap.data();
      if (!chatData.isGroup) {
        throw new Error('Sadece grup sohbetlerine kullanıcı eklenebilir.');
      }
      // Kullanıcıları ekle (arrayUnion ile)
      await updateDoc(chatRef, {
        users: arrayUnion(...userIds)
      });
      // (İsteğe bağlı) userDetails güncellemesi yapılabilir
      // Kullanıcı detaylarını güncelle (isteğe bağlı)
      // ...
    } catch (error) {
      //console.error('Grup sohbetine kullanıcı eklenirken hata:', error);
      throw error;
    }
  },

  /**
   * Gruptan kullanıcı çıkarır (kendi kendini veya izinli kullanıcı kodlarına sahip biri başkasını).
   * @param {string} chatId
   * @param {string} memberUserIdToRemove
   * @param {string} actorUserId
   */
  async removeUserFromGroup (chatId, memberUserIdToRemove, actorUserId) {
    if (!chatId || !memberUserIdToRemove || !actorUserId) {
      throw new Error('Geçersiz parametreler.')
    }
    const chatRef = doc(chatDb, 'chats', chatId)
    const chatSnap = await getDoc(chatRef)
    if (!chatSnap.exists()) {
      throw new Error('Sohbet bulunamadı.')
    }
    const chatData = chatSnap.data()
    if (!chatData.isGroup) {
      throw new Error('Sadece grup sohbetlerinde üye çıkarılabilir.')
    }
    const users = Array.isArray(chatData.users) ? chatData.users : []
    if (!users.includes(memberUserIdToRemove)) {
      throw new Error('Kullanıcı bu grupta değil.')
    }
    if (users.length <= 1) {
      throw new Error('Son üye gruptan çıkarılamaz.')
    }
    const isSelf = memberUserIdToRemove === actorUserId
    if (!isSelf) {
      const actorProfile = await userService.getUserProfile(actorUserId)
      if (!userCanRemoveOthersFromGroup(actorProfile)) {
        throw new Error('Bu işlem için yetkiniz yok.')
      }
    }
    await updateDoc(chatRef, {
      users: arrayRemove(memberUserIdToRemove),
      [`userDetails.${memberUserIdToRemove}`]: deleteField()
    })
  },

  uploadAndSendFile: async function(chatId, userId, file, type = 'file') {
    try {
      if (!chatId || !userId || !file) throw new Error('Eksik parametre');
      const userProfile = await userService.getUserProfile(userId);
      const maxSize = getMaxUploadSizeForProfile(userProfile);
      if (file.size > maxSize) throw new Error('Dosya büyük');
      const fileURL = await this.uploadFile(chatId, file);
      const additionalData = {
        fileName: file.name || 'file',
        fileSize: file.size,
        fileType: file.type,
        fileURL
      };
      let finalType = type;
      if (file.type.startsWith('image/')) finalType = 'image';
      else if (file.type.startsWith('video/')) finalType = 'video';
      else if (file.type.includes('pdf') || file.type.includes('doc') || file.type.includes('xls') || file.type.includes('ppt')) finalType = 'document';
      else finalType = 'file';
      //console.log('sendMessage çağrılıyor:', {chatId, userId, fileURL, finalType, additionalData});
      const msg = await this.sendMessage(chatId, userId, fileURL, finalType, additionalData);
      //console.log('sendMessage sonucu:', msg);
      return msg;
    } catch (error) {
      //console.error('Error uploading and sending file:', error);
      return undefined;
    }
  },

  /**
   * Son aktivitesi staleDays günü aşmış özel sohbetleri bu kullanıcı için arşivler.
   * Aktivite: lastMessageTimestamp (oluşturma anı da burada tutuluyor).
   * Okuma maliyeti: Mevcut onSnapshot sorgusu değişmediği sürece belge okuma sayısı aynı kalır;
   * dolaylı olarak eski sohbetler listeden düşer; ileride sorguda “aktif” alanı kullanılırsa okumalar düşürülebilir.
   */
  async autoArchiveStalePrivateChatsForUser (userId, chats, staleDays = STALE_PRIVATE_CHAT_DAYS_DEFAULT) {
    const staleMs = staleDays * 24 * 60 * 60 * 1000
    const cutoff = Date.now() - staleMs
    const stale = (chats || []).filter((chat) => {
      if (!chat || chat.isGroup || chat.isAnnouncement) return false
      const users = chat.users || []
      if (users.length !== 2 || !users.includes(userId)) return false
      const lastMs = firebaseTimestampToMs(chat.lastMessageTimestamp)
      if (!lastMs) return false
      return lastMs < cutoff
    })
    for (const chat of stale) {
      try {
        await this.archiveChat(chat.id, userId)
      } catch (err) {
        console.warn('Otomatik arşiv atlandı:', chat.id, err)
      }
    }
    return stale.length
  },

  /**
   * Sohbeti arşivler (sadece özel sohbetler için, kullanıcı bazlı)
   * @param {string} chatId - Arşivlenecek sohbet ID
   * @param {string} userId - İşlemi yapan kullanıcı ID
   * @returns {Promise<boolean>} - Başarılı olursa true
   */
  async archiveChat(chatId, userId) {
    try {
      // Sohbet bilgilerini al
      const chatRef = doc(chatDb, 'chats', chatId)
      const chatDoc = await getDoc(chatRef)
      
      if (!chatDoc.exists()) {
        throw new Error('Sohbet bulunamadı')
      }
      
      const chatData = chatDoc.data()
      
      // Duyuru veya grup sohbetlerinin arşivlenmesini engelle
      if (chatData.isAnnouncement || chatData.isGroup) {
        throw new Error('Duyurular ve grup sohbetleri arşivlenemez')
      }
      
      // Mevcut arşivleme listesini al (yoksa boş array)
      const archivedBy = chatData.archivedBy || []
      
      // Kullanıcı zaten arşivlemişse bir şey yapma
      if (archivedBy.includes(userId)) {
        return true
      }
      
      // Kullanıcıyı arşivleme listesine ekle
      archivedBy.push(userId)
      
      // Sohbetteki tüm kullanıcılar arşivlemişse, tamamen arşive taşı
      const allUsers = chatData.users || []
      const allArchived = allUsers.every(uid => archivedBy.includes(uid))
      
      if (allArchived) {
        // Tüm kullanıcılar arşivledi, artık tamamen archive collection'ına taşıyabiliriz
        
        // Sohbetteki tüm mesajları al
        const messagesRef = collection(chatDb, `chats/${chatId}/messages`)
        const messagesSnapshot = await getDocs(messagesRef)
        
        const messages = []
        messagesSnapshot.forEach(doc => {
          messages.push({
            id: doc.id,
            ...doc.data()
          })
        })
        
        // Arşiv verisini hazırla
        const archiveData = {
          chatId,
          chatData: {
            ...chatData,
            archivedBy,
            finalArchivedAt: serverTimestamp()
          },
          messages,
          createdAt: serverTimestamp()
        }
        
        // Archive collection'ına ekle
        await addDoc(collection(chatDb, 'archive'), archiveData)
        
        // Orijinal mesajları sil
        const batch = writeBatch(chatDb)
        messagesSnapshot.forEach(doc => {
          batch.delete(doc.ref)
        })
        await batch.commit()
        
        // Orijinal sohbeti sil
        await deleteDoc(chatRef)
      } else {
        // Sadece bu kullanıcı için arşivle, sohbeti sakla
        await updateDoc(chatRef, {
          archivedBy: archivedBy,
          [`archivedAt_${userId}`]: serverTimestamp()
        })
      }
      
      return true
    } catch (error) {
      console.error('Sohbet arşivlenirken hata:', error)
      throw error
    }
  },

  async assertActorIsAdmin (actorUserId) {
    if (!actorUserId) {
      throw new Error('Oturum bulunamadı.')
    }
    if (auth.currentUser && auth.currentUser.uid !== actorUserId) {
      throw new Error('Bu işlem yalnızca oturumdaki admin hesabıyla yapılabilir.')
    }
    const profile = await userService.getUserProfile(actorUserId)
    if (!isAdminProfile(profile)) {
      throw new Error('Bu işlem sadece admin kullanıcılar için.')
    }
  },

  /**
   * Grubu mesajları, dosyaları ve sohbet kaydıyla birlikte kalıcı siler.
   * Yalnızca admin. Özel sohbetlere uygulanmaz.
   */
  async closeGroupCompletely (chatId, actorUserId, onProgress) {
    let groupLockHeld = false
    try {
      if (!chatId || !actorUserId) {
        throw new Error('Geçersiz parametre.')
      }
      acquireMaintenance('group')
      groupLockHeld = true
      await this.assertActorIsAdmin(actorUserId)
      const chatRef = doc(chatDb, 'chats', chatId)
      const chatSnap = await getDoc(chatRef)
      if (!chatSnap.exists()) {
        throw new Error('Grup bulunamadı.')
      }
      const chatData = chatSnap.data()
      if (!chatData.isGroup && !chatData.isAnnouncement) {
        throw new Error('Sadece gruplar kapatılabilir.')
      }

      const messages = await drainCollection(
        collection(chatDb, 'chats', chatId, 'messages'),
        {
          deleteFiles: true,
          onProgress: (progress) => {
            if (typeof onProgress === 'function') {
              onProgress({
                phase: 'messages',
                deletedDocs: progress.deletedDocs,
                deletedFiles: progress.deletedFiles
              })
            }
          }
        }
      )
      if (messages.incomplete) {
        throw new Error('Grup çok büyük, kapatma yarım kaldı. Aynı işlemi tekrar çalıştırın.')
      }

      const typing = await drainCollection(
        collection(chatDb, 'chats', chatId, 'typing'),
        { deleteFiles: false }
      )
      if (typing.incomplete) {
        throw new Error('Grup çok büyük, kapatma yarım kaldı. Aynı işlemi tekrar çalıştırın.')
      }

      let folderFiles = 0
      try {
        folderFiles = await deleteStorageUnder(`chatFiles/${chatId}`)
      } catch (error) {
        console.warn('Grup dosya klasörü silinemedi:', error?.code || error?.message)
      }

      await deleteDoc(chatRef)
      chatMetadataCache.delete(chatId)

      return {
        groupName: chatData.groupName || 'Grup Sohbeti',
        deletedMessages: messages.deletedDocs,
        deletedFiles: messages.deletedFiles + folderFiles
      }
    } catch (error) {
      rethrowFriendly(error)
    } finally {
      if (groupLockHeld) {
        releaseMaintenance()
      }
    }
  }
};


export { auth, authDb, chatDb, authStorage, chatStorage };
export default { authApp, chatApp }; 